// ===== Packages =====

import React, {
    useCallback,
    useLayoutEffect,
    useMemo,
    useState,
    useEffect,
    useRef,
}                                                       from 'react';
import styled, { DefaultTheme }                         from 'styled-components';
import { ReactSVG }                                     from 'react-svg';
import {
    flushSync,
    unstable_batchedUpdates as batchUpdates,
}                                                       from 'react-dom';
import {
    Slate,
    Editable,
    withReact,
    ReactEditor,
}                                                       from 'slate-react';
import {
    createEditor,
    Node,
    Path,
    Transforms,
    Editor,
    Descendant,
    Location,
    NodeEntry,
    Text,
    Selection,
    Range as SlateRange,
}                                                       from 'slate';
import { withHistory }                                  from 'slate-history';
import { useDropzone }                                  from 'react-dropzone';
import { v4 as uuidv4 }                                 from 'uuid';
import {
    UploadTask,
    StorageError,
    getDownloadURL,
    UploadTaskSnapshot,
    StorageReference,
}                                                       from 'firebase/storage';
import ShortUniqueId                                    from 'short-unique-id';
// eslint-disable-next-line import/no-extraneous-dependencies
import mime                                             from 'mime';

// ===== Components =====

import { Transition }                                   from '../../Transition';
import {
    SlateLeaf,
    SlatePlaceholder,
    DropzoneInput,
}                                                       from '../../../styles';
import Modal                                            from '../../Modal';
import Tooltip                                          from '../../Tooltip';
import {
    Button,
    UploadingMediaItem,
    GenericModal,
    EmojiSelector,
    PortableToolbar,
    StationaryToolbar,
}                                                       from '../helpers';
import {
    BottomBufferElement,
    BottomBufferHelpers,
    withBottomBuffers,
}                                                       from '../plugins/bottomBuffersPlugin';
import { EMPHASIZER_MARK }                              from '../plugins/enums';
import {
    withDividers,
    DividerHelpers,
    DividerElement,
}                                                       from '../plugins/dividersPlugin';
import {
    EmphasizerHelpers,
}                                                       from '../plugins/emphasizersPlugin';
import {
    withHeaders,
    HeaderHelpers,
    HeaderElement,
}                                                       from '../plugins/headersPlugin';
import {
    LineHelpers,
    withLines,
}                                                       from '../plugins/linesPlugin';
import {
    withAudioNotes,
    AudioNoteHelpers,
    AudioNoteElement,
}                                                       from '../plugins/audioNotesPlugin';
import {
    withWebLinks,
    WebLinkHelpers,
    WebLinkElement,
}                                                       from '../plugins/webLinksPlugin';
import {
    withLists,
    ListHelpers,
    ListElement,
    ListItemElement,
}                                                       from '../plugins/listsPlugin';
import {
    withFigures,
    FigureHelpers,
    CaptionElement,
    FigureElement,
    FigureContentElement,
    FigureContentTypes,
}                                                       from '../plugins/figuresPlugin';
import {
    withBlockQuotes,
    BlockQuoteHelpers,
    BlockQuoteElement,
}                                                       from '../plugins/blockQuotesPlugin';
import {
    handleEditorClipboardPaste,
    withRichTextClipboard,
}                                                       from '../plugins/richTextClipboardPlugin';
import HandleOnKeyDown                                  from '../plugins/handleHotKeys';
import {
    BlockQuote,
    BottomBuffer,
    Caption,
    Divider,
    Figure,
    HeaderOne,
    HeaderTwo,
    Image,
    AudioNote,
    InlineLink,
    List,
    ListItem,
    Paragraph,
    YouTubeEmbed,
    TwitterEmbed,
    SpotifyEmbed,
    VimeoEmbed,
}                                                       from '../renderers';
import {
    resolveAnnotationSubmitButtonDisabledReason,
    resolveMobileAnnotationEditorKeyboardOffset,
    resolveStationaryToolbarLifecycleDelay,
    resolveStationaryToolbarToggleAction,
    resolveStationaryToolbarVisibilityAction,
}                                                       from './helpers';

// ===== Services =====

import {
    getMediaStorageBucket,
    setColorLightness,
    uploadToCloudStorage,
    playAudio,
    getStorageErrorMessage,
    binarySearchIndex,
    copyToClipboard,
    recordUserAction,
    fontColorDiscriminator,
    createMedia,
    resolveEditorSelectionText,
    serializeSlateSelectionForClipboard,
    getPressEventHandlers,
    getTouchMouseEventHandlers,
    isTestMode,
    replaceSlateEditorValue,
    detectTouchDevice,
} from '../../../services';
import buildAnnotationQuote                              from '../../../services/buildAnnotationQuote';
import getOrCreateAudioClip                              from '../../../services/getOrCreateAudioClip';
import logAnnotationEditDebug                            from '../../../services/logAnnotationEditDebug';

// ===== Hooks =====

import {
    useTimeout,
    useEventListener,
}                                                       from '../../../hooks';

// ===== Enums =====

import {
    ANNOTATION_EDITOR_CLOSE_REASON,
    EDITOR_TOOLBAR_TYPE,
    EDITOR_TOOLBAR_TOOL_GROUP,
    TOOLTIP_TYPE,
    EDITOR_CONTEXT_TYPE,
    STORAGE_ENTITY,
    MEDIA_TYPE,
    CURSOR_TARGET,
    INTERACTABLE_OBJECT,
    STORAGE_ERROR_CODE,
    BUTTON_TYPE,
    USER_ACTION_TYPE,
    AUTHENTICATION_TYPE,
    SLATE_EDITOR_REPLACEMENT_SELECTION,
}                                                       from '../../../enums';
import { ZOOM_LEVEL }                                   from '../elements/enums';

// ===== Interfaces =====

import {
    AudioNoteNode,
    FigureNode,
    WebLinkNode,
}                                                       from '../interfaces';
import {
    ICoord,
    IMediaItem,
    IModalMessage,
    IAnnotationQuote,
    IAnnotationEditorSelectionPlacementHint,
    IResolvedSlateSelectionFromNativeSelection,
    IReadHighlightSelectionResolution,
    IReadHighlightSelectionSourceArgs,
    IReadHighlightSelectionDebugArgs,
    IHandleHighlightPostTextResult,
    IDimension,
    IPartialUserItem,
    ISnackbarItem,
    IEditorDecoration,
    IEditorDictationToken,
    ITranscriptionWord,
    IAnnotationValue,
    IZoomLevelItem,
}                                                       from '../../../interfaces';

// ===== Sounds =====

import InputClick                                       from '../../../sounds/button_click.mp3';

// ===== Images =====

import ArrowIcon                                        from '../../../images/editor/arrow.svg';
import ToolsIcon                                        from '../../../images/editor/tools.svg';
import SmileyIcon                                       from '../../../images/editor/smiley.svg';
import UploadIcon                                       from '../../../images/editor/upload.svg';
import CrossIcon                                        from '../../../images/editor/cross.svg';
import MicrophoneIcon                                   from '../../../images/editor/mic.svg';
import CheckmarkIcon                                    from '../../../images/editor/checkmark.svg';
import ClipboardIcon                                    from '../../../images/editor/clipboard.svg';
import CautionIcon                                      from '../../../images/caution.svg';
import FileIcon                                         from '../../../images/file.svg';

// ===== Constants =====

import {
    BODY_FONT_SIZE,
    INITIAL_EDITOR_VALUE,
    SERIALIZED_TRUE_INITIAL_EDITOR_VALUE,
    SERIALIZED_INITIAL_EDITOR_VALUE,
    BUTTON_CONTAINER_LIGHTNESS_VALUE_HOVER,
    MAX_FADE_IN_STAGGER_TRANSITION_DURATION,
    FADE_IN_STAGGER_TRANSITION_DURATION,
    FADE_IN_STAGGER_OFFSET_DURATION,
    FADE_IN_DEFAULT_STYLE,
    FADE_IN_TRANSITION_STYLES,
    FADE_IN_TRANSITION_DURATION,
    FADE_IN_STAGGER_DEFAULT_STYLE,
    FADE_IN_STAGGER_TRANSITION_STYLES,
    DEFAULT_AUDIO_VOLUME,
    HOVER_TARGET_CLASSNAME,
    ANNOTATION_EDITOR_Z_INDEX,
    DEFAULT_ANNOTATION_EDITOR_HEIGHT,
    SLATE_EDITOR_CLASSNAME,
    NANOSECONDS_IN_A_SECOND,
    MILLISECONDS_IN_A_SECOND,
    REMOVE_LEADING_AND_TRAILING_PUNCTUATION_REGEX,
    DEFAULT_SNACKBAR_VISIBLE_DURATION,
    SLATE_EDITOR_SELECT_ALL_TEXT_TIMEOUT_DURATION,
    CONTENTS_TABLE_Z_INDEX,
    USER_VERIFY_EMAIL_BUTTON_TEXT,
    EXTENDED_SNACKBAR_VISIBLE_DURATION,
    SUPPORT_EMAIL_ADDRESS,
    HIDDEN_VOID_CONTENT_STYLE,
}                                                       from '../../../constants/generalConstants';
import { SAVE_POST_DURING_AUDIO_RECORDING_ERROR }       from '../../../constants/notificationMessages';
import CURSOR_SIGN                                      from '../../../constants/cursorSigns';
import KEYCODE                                          from '../../../constants/keycodes';
import FONT_TYPE                                        from '../../../constants/fontType';
import MEDIA_QUERY_SIZE                                 from '../../../constants/mediaQuerySizes';
import {
    MAX_FADE_IN_STAGGER_TRANSITION_DURATION as STATIONARY_TOOLBAR_MAX_STAGGER_TRANSITION_DURATION,
    TOOL_GROUPS_COUNT,
    TOOL_MARGIN,
    TOOL_TRANSITION_DURATION,
}                                                       from '../helpers/constants';

// ===== Styles =====

import { theme as themeObj }                            from '../../../themes/theme-context';

const EDITOR_PLUGINS = [
    withFigures,
    withLists,
    withHeaders,
    withLines,
    withWebLinks,
    withBlockQuotes,
    withBottomBuffers,
    withDividers,
    withReact,
    withHistory,
    withRichTextClipboard,
    withAudioNotes,
];
const SCROLL_FOLLOW_POSITION_TRANSITION_DURATION = 48;
const DEBUG_TEXT_PREVIEW_MAX_LENGTH = 120;
const READER_ANNOTATION_OPEN_SYNC_TIMEOUT_MS = 250;

/**
 * Collapses a DOM selection into the placement facts ReaderView needs for the
 * floating desktop annotation editor. This runs at highlight-open time, before
 * the painted quote decorators can flatten wrapped text into a single element
 * box and hide the original drag direction.
 */
const resolveAnnotationEditorSelectionPlacementHint = ({
    editor,
    selection,
}: {
    editor: Editor,
    selection: SlateRange,
}): IAnnotationEditorSelectionPlacementHint => {
    const domRange = ReactEditor.toDOMRange(editor, selection);
    const rects = Array.from(domRange.getClientRects());
    const firstRect = rects[0];
    const lastRect = rects[rects.length - 1];
    const selectionIsMultiLine = rects.length > 1
        && !!firstRect
        && !!lastRect
        && (
            Math.abs(lastRect.top - firstRect.top) > 1
            || Math.abs(lastRect.bottom - firstRect.bottom) > 1
        );

    return {
        selectionIsBackward: SlateRange.isBackward(selection),
        selectionIsMultiLine,
    };
};

/**
 * Prevents mobile scroll gestures from regenerating the same visible highlight
 * quote when the DOM selection still points at the already-open annotation.
 */
const annotationQuoteMatchesSelection = ({
    quote,
    selection,
    text,
}: {
    quote?: IAnnotationQuote | null,
    selection: Selection,
    text: string,
}): boolean => {
    if (
        !quote
        || quote.selections.length !== 1
        || quote.texts.length !== 1
    ) {
        return false;
    }

    const [quoteSelection] = quote.selections;
    const [quoteText] = quote.texts;
    if (!quoteSelection || !selection) {
        return false;
    }

    const sameDirection = (
        Path.equals(quoteSelection.anchor.path, selection.anchor.path)
        && quoteSelection.anchor.offset === selection.anchor.offset
        && Path.equals(quoteSelection.focus.path, selection.focus.path)
        && quoteSelection.focus.offset === selection.focus.offset
    );
    const reversedDirection = (
        Path.equals(quoteSelection.anchor.path, selection.focus.path)
        && quoteSelection.anchor.offset === selection.focus.offset
        && Path.equals(quoteSelection.focus.path, selection.anchor.path)
        && quoteSelection.focus.offset === selection.anchor.offset
    );

    return quoteText === text && (sameDirection || reversedDirection);
};

/**
 * Keep debug text previews short so selection traces stay readable in the
 * console and in the runtime ring buffer.
 */
const resolveDebugTextPreview = (text?: string | null): string => (
    text?.trim().slice(0, DEBUG_TEXT_PREVIEW_MAX_LENGTH) || ''
);

/**
 * Surface the nearest useful DOM landmarks for a native selection endpoint so
 * deep reader highlight failures are easier to diagnose.
 */
const resolveNativeSelectionNodeDebugLabel = (node: globalThis.Node | null): string | null => {
    if (!node) {
        return null;
    }

    const ownerElement = (
        typeof globalThis.Node !== 'undefined'
        && node.nodeType === globalThis.Node.TEXT_NODE
    )
        ? node.parentElement
        : node as Element | null;

    const paragraphId = ownerElement
        ?.closest('[data-paragraph-id]')
        ?.getAttribute('data-paragraph-id');
    const slateNode = ownerElement
        ?.closest('[data-slate-node]')
        ?.getAttribute('data-slate-node');
    const textPreview = resolveDebugTextPreview(node.textContent);

    return [
        node.nodeName.toLowerCase(),
        paragraphId ? `paragraph=${paragraphId}` : null,
        slateNode ? `slate=${slateNode}` : null,
        textPreview ? `text=${textPreview}` : null,
    ].filter(Boolean).join(' | ') || null;
};

/**
 * Serialize the browser selection into primitive values so we can compare it to
 * the Slate selections derived from it.
 */
const resolveNativeSelectionDebugPayload = (
    selection: globalThis.Selection | null,
): Record<string, unknown> => {
    if (!selection) {
        return { exists: false };
    }

    const selectionText = selection.toString();

    return {
        exists: true,
        isCollapsed: selection.isCollapsed,
        rangeCount: selection.rangeCount,
        textLength: selectionText.length,
        textPreview: resolveDebugTextPreview(selectionText),
        anchorOffset: selection.anchorOffset,
        focusOffset: selection.focusOffset,
        anchorNode: resolveNativeSelectionNodeDebugLabel(selection.anchorNode),
        focusNode: resolveNativeSelectionNodeDebugLabel(selection.focusNode),
    };
};

/**
 * Flatten a Slate range into stable primitives so the debug logs can show
 * whether the exact, loose, or cached selection path won.
 */
const resolveSlateSelectionDebugPayload = (
    selection: Selection | null,
): Record<string, unknown> => {
    if (!selection) {
        return { exists: false };
    }

    return {
        exists: true,
        anchorPath: selection.anchor.path.join('.'),
        anchorOffset: selection.anchor.offset,
        focusPath: selection.focus.path.join('.'),
        focusOffset: selection.focus.offset,
        isBackward: SlateRange.isBackward(selection),
        isCollapsed: SlateRange.isCollapsed(selection),
    };
};

/**
 * Record whether the live DOM selection mapped exactly, loosely, or not at all
 * so deep-reader selection failures are visible in the trace.
 */
const resolveSlateSelectionFromNativeSelectionWithMatchMode = ({
    editor,
    nativeSelection,
}: {
    editor: Editor,
    nativeSelection: globalThis.Selection | null,
}): IResolvedSlateSelectionFromNativeSelection => {
    if (!nativeSelection) {
        return {
            matchMode: 'none',
            selection: null,
        };
    }

    const exactSelection = ReactEditor.toSlateRange(
        editor,
        nativeSelection,
        { exactMatch: true, suppressThrow: true },
    ) as Selection | null;
    if (exactSelection) {
        return {
            matchMode: 'exact',
            selection: exactSelection,
        };
    }

    const looseSelection = ReactEditor.toSlateRange(
        editor,
        nativeSelection,
        { exactMatch: false, suppressThrow: true },
    ) as Selection | null;
    if (looseSelection) {
        return {
            matchMode: 'loose',
            selection: looseSelection,
        };
    }

    return {
        matchMode: 'none',
        selection: null,
    };
};

/**
 * Collapse the derived/cached selection resolution into a short label so each
 * debug entry tells us which path actually produced the winning range.
 */
const resolveReadHighlightSelectionSource = ({
    cachedSelection,
    derivedSelection,
    derivedSelectionMatchMode,
    resolvedSelection,
}: IReadHighlightSelectionSourceArgs): string => {
    if (!resolvedSelection) {
        return 'none';
    }

    if (
        !!derivedSelection
        && SlateRange.equals(resolvedSelection.selection, derivedSelection)
    ) {
        return derivedSelectionMatchMode === 'exact'
            ? 'derived-exact'
            : 'derived-loose';
    }

    if (
        !!cachedSelection
        && SlateRange.equals(resolvedSelection.selection, cachedSelection)
    ) {
        return 'cached';
    }

    return 'unknown';
};

/**
 * Share the highlight selection resolution bookkeeping across the main mouseup
 * flow and the dismissal mouseup guard so their logs stay comparable.
 */
const resolveReadHighlightSelection = ({
    cachedSelection,
    editor,
    nativeSelection,
}: {
    cachedSelection: Selection | null,
    editor: Editor,
    nativeSelection: globalThis.Selection | null,
}): IReadHighlightSelectionResolution => {
    const {
        matchMode: derivedSelectionMatchMode,
        selection: derivedSelection,
    } = resolveSlateSelectionFromNativeSelectionWithMatchMode({
        editor,
        nativeSelection,
    });
    const resolvedSelection = resolveEditorSelectionText({
        editor,
        selection: cachedSelection,
        deriveSelection: () => derivedSelection,
    });

    return {
        cachedSelection,
        derivedSelection,
        derivedSelectionMatchMode,
        resolvedSelection,
        resolvedSelectionSource: resolveReadHighlightSelectionSource({
            cachedSelection,
            derivedSelection,
            derivedSelectionMatchMode,
            resolvedSelection,
        }),
    };
};

/**
 * Emit one compact selection snapshot per checkpoint so we can line up the DOM
 * selection, the Slate mapping, and the action taken afterward.
 */
const logReadHighlightSelectionDebug = ({
    captureQuoteWithoutOpeningEditor = false,
    checkpoint,
    dismissFromOutsideInteraction = false,
    event,
    nativeSelection,
    postQuote = null,
    reason,
    resolvedSelection,
    resolvedSelectionSource,
    cachedSelection,
    derivedSelection,
    derivedSelectionMatchMode,
    selectionInsideAnnotationEditor = false,
    selectionMatchesVisibleHighlight = false,
    selectionPlacementHint,
    showAnnotationEditor,
    touchGestureMoved = false,
}: IReadHighlightSelectionDebugArgs): void => {
    logAnnotationEditDebug(event, {
        checkpoint,
        reason,
        showAnnotationEditor,
        captureQuoteWithoutOpeningEditor,
        dismissFromOutsideInteraction,
        selectionInsideAnnotationEditor,
        selectionMatchesVisibleHighlight,
        touchGestureMoved,
        derivedSelectionMatchMode,
        resolvedSelectionSource,
        nativeSelection: resolveNativeSelectionDebugPayload(nativeSelection),
        cachedSelection: resolveSlateSelectionDebugPayload(cachedSelection),
        derivedSelection: resolveSlateSelectionDebugPayload(derivedSelection),
        resolvedSelection: resolveSlateSelectionDebugPayload(resolvedSelection?.selection || null),
        resolvedTextLength: resolvedSelection?.text.length || 0,
        resolvedTextPreview: resolveDebugTextPreview(resolvedSelection?.text),
        hasPostQuote: !!postQuote,
        postQuoteTextLength: postQuote?.texts[0]?.length || 0,
        postQuoteTextPreview: resolveDebugTextPreview(postQuote?.texts[0]),
        selectionPlacementHint: selectionPlacementHint
            ? {
                selectionIsBackward: selectionPlacementHint.selectionIsBackward,
                selectionIsMultiLine: selectionPlacementHint.selectionIsMultiLine,
            }
            : null,
    });
};

/**
 * Docked mobile readers should snap viewport-offset changes immediately, while
 * desktop scroll-follow keeps the shorter animated easing path.
 */
const resolveAnnotationEditorPositionTransitionDuration = ({
    disablePositionTransition,
    dockToViewport,
    transitionDuration,
}: {
    disablePositionTransition: boolean,
    dockToViewport: boolean,
    transitionDuration: number,
}): number => {
    if (dockToViewport) {
        return 0;
    }

    if (disablePositionTransition) {
        return SCROLL_FOLLOW_POSITION_TRANSITION_DURATION;
    }

    return transitionDuration;
};

/**
 * Docked viewport pinning and scroll-follow should both avoid overshoot easing
 * so annotation positioning stays visually stable during reader movement.
 */
const resolveAnnotationEditorPositionTransitionEasing = ({
    disablePositionTransition,
    dockToViewport,
    theme,
}: {
    disablePositionTransition: boolean,
    dockToViewport: boolean,
    theme: DefaultTheme,
}): string => {
    if (dockToViewport || disablePositionTransition) {
        return 'linear';
    }

    return theme.motion.overshoot;
};

/**
 * Both floating and docked mobile annotation editors anchor from an explicit
 * bottom offset. Floating editors track their free-positioning floor, while
 * docked mobile editors use the same measured keyboard-safe lift inside an
 * inert fixed overlay root.
 */
const resolveAnnotationEditorBottomCss = ({
    mobileKeyboardOffset,
}: {
    mobileKeyboardOffset: number,
}): string => `bottom: ${mobileKeyboardOffset}px;`;

AnnotationEditor.defaultProps = {
    isEditing: false,
    message: undefined,
    userIsAdmin: false,
    updatePosition: undefined,
    setRef: null,
    placeholder: null,
    shouldFocus: false,
    isAuthor: false,
    disablePositionTransition: false,
    submitAnnotation: undefined,
    submitAnnotationComment: undefined,
    editAnnotation: undefined,
    cancelEditAnnotation: undefined,
    top: null,
    left: null,
    viewportDimensions: null,
    postEditor: null,
    postQuote: null,
    setPostQuote: undefined,
    deferReaderSelectionFocusUntilPostQuotePaint: false,
    captureQuoteWithoutOpeningEditor: false,
    initialDraftValue: undefined,
    persistDraftValue: undefined,
    showAnnotationEditorOverride: false,
    width: null,
    setEditor: undefined,
    boxShadow: false,
    remeasureParentHeight: undefined,
    dictationPlaying: false,
    transcript: undefined,
    timeElapsed: null,
    isCommentEditor: false,
    isComment: false,
    backgroundColor: undefined,
    highlightColor: undefined,
    onHoverEnter: undefined,
    handleShowAnnotationEditor: undefined,
    setIsUserProfileDialogExpanded: undefined,
    setNotifyUserToSignUpAfterAnnotationCreationAttempt: undefined,
    annotationIsFocused: undefined,
    pauseReadOnlyAudioPlayback: undefined,
};
interface Props {
    customCursorActive: boolean,
    id: string,
    user: IPartialUserItem | null,
    userIsAdmin?: boolean,
    userEmailVerified: boolean,
    hasSound: boolean,
    readOnly: boolean,
    isEditing?: boolean,
    currentSessionId: string | null,
    currentZoomLevel: IZoomLevelItem | null,
    boxShadow?: boolean,
    type: EDITOR_CONTEXT_TYPE,
    color: string,
    fontMultiplier: number,
    interactionDisabled: boolean,
    width?: number,
    message?: IAnnotationValue,
    top?: number | null,
    left?: number | null,
    annotationIsFocused?: boolean,
    submitAnnotation?: (
        value: string,
        text: string,
        quote: IAnnotationQuote,
        media: string[],
    ) => void,
    submitAnnotationComment?: (
        value: string,
        text: string,
        media: string[],
    ) => boolean,
    editAnnotation?: (
        value: string,
        text: string,
        media: string[],
    ) => void,
    cancelEditAnnotation?: () => void,
    shouldFocus?: boolean,
    isAuthor?: boolean,
    disablePositionTransition?: boolean,
    placeholder?: string,
    onCursorEnter: (
        targetType: CURSOR_TARGET | INTERACTABLE_OBJECT | string,
        actions: string[],
        candidateTarget?: HTMLElement,
    ) => void,
    onCursorLeave: (e?: React.MouseEvent | React.TouchEvent | React.SyntheticEvent) => void,
    setInputFocused: React.Dispatch<React.SetStateAction<boolean>>,
    updatePosition?: (
        recomputePlacement?: boolean,
        selectionPlacementHint?: IAnnotationEditorSelectionPlacementHint,
    ) => void,
    initialDraftValue?: Descendant[] | null,
    persistDraftValue?: (value: Descendant[] | null) => void,
    showAnnotationEditorOverride?: boolean,
    setRef?: (ref: HTMLDivElement) => void,
    postEditor?: Editor | null,
    postQuote?: IAnnotationQuote | null,
    setPostQuote?: React.Dispatch<React.SetStateAction<IAnnotationQuote | null>>,
    deferReaderSelectionFocusUntilPostQuotePaint?: boolean,
    captureQuoteWithoutOpeningEditor?: boolean,
    viewportDimensions?: IDimension,
    postId: string,
    setEditor?: (editor: Editor) => void,
    setSnackbarData: React.Dispatch<React.SetStateAction<ISnackbarItem>>,
    remeasureParentHeight?: () => void,
    dictationPlaying?: boolean,
    transcript?: any,
    timeElapsed?: number,
    isCommentEditor?: boolean,
    isComment?: boolean,
    backgroundColor?: string,
    highlightColor?: string,
    onHoverEnter?: (e: React.MouseEvent) => void,
    handleShowAnnotationEditor?: (show: boolean) => void,
    setIsUserProfileDialogExpanded?: (isExpanded: boolean) => void,
    setNotifyUserToSignUpAfterAnnotationCreationAttempt?: React.Dispatch<React.SetStateAction<boolean>>,
    pauseReadOnlyAudioPlayback?: () => void,
}

/**
 * Counts top-level Slate nodes from a detached value snapshot.
 */
function getSlateValueNodeCount(nodes: Descendant[] | null | undefined): number {
    return Array.isArray(nodes) ? nodes.length : 0;
}

/**
 * Reads plain text from a detached Slate value without mutating the live editor.
 */
function getSlateValueTextLength(nodes: Descendant[] | null | undefined): number {
    if (!Array.isArray(nodes) || nodes.length === 0) {
        return 0;
    }

    const detachedEditor = {
        children: nodes,
    } as Editor;
    return Node.string(detachedEditor).length;
}

function AnnotationEditor({
    customCursorActive,
    id,
    user,
    userIsAdmin = false,
    userEmailVerified,
    top = null,
    left = null,
    annotationIsFocused,
    hasSound,
    readOnly,
    isEditing,
    currentSessionId,
    currentZoomLevel,
    boxShadow,
    type,
    color,
    fontMultiplier,
    interactionDisabled,
    width,
    message,
    submitAnnotation,
    submitAnnotationComment,
    editAnnotation,
    shouldFocus = false,
    isAuthor,
    disablePositionTransition = false,
    placeholder,
    onCursorEnter,
    onCursorLeave,
    setInputFocused,
    updatePosition,
    initialDraftValue,
    persistDraftValue,
    showAnnotationEditorOverride,
    setRef,
    postEditor,
    postQuote,
    setPostQuote,
    deferReaderSelectionFocusUntilPostQuotePaint = false,
    captureQuoteWithoutOpeningEditor = false,
    viewportDimensions,
    postId,
    setEditor,
    setSnackbarData,
    remeasureParentHeight,
    dictationPlaying,
    transcript,
    timeElapsed,
    cancelEditAnnotation,
    isCommentEditor,
    isComment,
    backgroundColor,
    highlightColor,
    onHoverEnter,
    handleShowAnnotationEditor,
    setIsUserProfileDialogExpanded,
    setNotifyUserToSignUpAfterAnnotationCreationAttempt,
    pauseReadOnlyAudioPlayback,
}: Props): JSX.Element {
    // ===== General Constants =====

    const EDITOR_BUTTON_CONTAINER_HEIGHT = 2.5 * BODY_FONT_SIZE;
    const COPY_TEXT_BUTTON_CONTAINER_HEIGHT = 1.875 * BODY_FONT_SIZE;
    const EDITOR_BUTTON_CONTAINER_PADDING = 0.3125 * BODY_FONT_SIZE;
    const EDITOR_BUTTON_LENGTH = EDITOR_BUTTON_CONTAINER_HEIGHT - 2 * (EDITOR_BUTTON_CONTAINER_PADDING);
    const EDITOR_BUTTON_PADDING = 0.3125 * BODY_FONT_SIZE;
    const EDITOR_BUTTON_MARGIN = 0.3125 * BODY_FONT_SIZE;
    const NUM_EDITOR_BUTTONS = 4;
    const EMOJI_SELECTOR_HEIGHT = 12.5 * BODY_FONT_SIZE;
    const SLATE_CONTAINER_DRAG_ACTIVE_MARGIN_RIGHT = 1.875 * BODY_FONT_SIZE;
    const STATIONARY_EDITOR_MIN_HEIGHT = EDITOR_BUTTON_CONTAINER_HEIGHT;
    const AUDIO_NOTE_AUTO_RECORD_IDENTIFIER = 'https://verasco.pe/auto-record-audio-note';
    const TOOLBAR_BUTTON_LENGTH = EDITOR_BUTTON_LENGTH - 2 * TOOL_MARGIN;
    // Docked reader layouts pin the annotation editor to the viewport bottom, so
    // the copy-highlight action needs to sit above the editor instead of off to the side.
    const mobileViewportEligible = !!viewportDimensions
        && viewportDimensions.width < MEDIA_QUERY_SIZE.medium.min;
    const dockCopyTextButtonAboveEditor = mobileViewportEligible;
    let copyTextTooltipSide = TOOLTIP_TYPE.bottom;
    if (dockCopyTextButtonAboveEditor) {
        copyTextTooltipSide = TOOLTIP_TYPE.left;
    } else if (isCommentEditor) {
        copyTextTooltipSide = TOOLTIP_TYPE.top;
    }
    const SNACKBAR_MESSAGE_COPY_HIGHLIGHT_TEXT_SUCCESS = 'Copied highlighted text to clipboard!';
    const SNACKBAR_MESSAGE_COPY_HIGHLIGHT_TEXT_ERROR = 'There was a problem copying highlighted text to your clipboard.';
    const SNACKBAR_MESSAGE_HIGHLIGHT_HEADER_TEXT_ERROR = 'Headers cannot be annotated.';
    const SLATE_PLACEHOLDER_FONT_MULTIPLIER = 0.9;
    const TOUCH_TAP_MAX_MOVEMENT_PX = 10;
    /**
     * Match the stationary toolbar shell timing to the real group Transition
     * timing, including the always-rendered expand-all control.
     */
    const stationaryToolbarLifecycleDelay = resolveStationaryToolbarLifecycleDelay({
        activeToolGroupCount: TOOL_GROUPS_COUNT,
        staggerOffsetDuration: FADE_IN_STAGGER_OFFSET_DURATION,
        toolTransitionDuration: TOOL_TRANSITION_DURATION,
        maxTransitionDuration: STATIONARY_TOOLBAR_MAX_STAGGER_TRANSITION_DURATION,
    });

    /**
     * Narrows a DOM event to TouchEvent in the browser-only interaction paths
     * that drive mobile annotation selection and dismissal behavior. Some
     * real-browser harnesses and WebDriver bridges expose touch-shaped events
     * without constructing a concrete TouchEvent instance, so accept either a
     * native TouchEvent or an Event carrying touch lists.
     */
    const resolveTouchEvent = (event: Event): TouchEvent | null => {
        if (typeof TouchEvent !== 'undefined' && event instanceof TouchEvent) {
            return event;
        }

        const maybeTouchEvent = event as Event & Partial<TouchEvent>;
        if (
            typeof maybeTouchEvent.touches !== 'undefined'
            || typeof maybeTouchEvent.changedTouches !== 'undefined'
        ) {
            return maybeTouchEvent as TouchEvent;
        }

        return null;
    };

    // ===== Ref =====

    const containerRef = useRef<HTMLDivElement>(null);
    const selectionToolbarRef = useRef<HTMLDivElement | null>(null);
    const mouseGestureRef = useRef<{
        x: number,
        y: number,
        moved: boolean,
    } | null>(null);
    const dismissAnnotationEditorOnMouseUpRef = useRef<boolean>(false);
    const dismissedAnnotationQuoteRef = useRef<IAnnotationQuote | null>(null);
    const desktopSelectionCollapseRecheckTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const suppressAnnotationEditorDismissUntilRef = useRef<number>(0);
    const touchGestureRef = useRef<{
        x: number,
        y: number,
        moved: boolean,
    } | null>(null);

    // ----- Sound Clips
    const inputClickClip = useRef<HTMLAudioElement | null>(null);

    // ===== State =====

    const getSavedSlateValue = (): Descendant[] => {
        /**
         * Breakpoint flips remount the annotation editor because ReaderView
         * switches between the floating inline render and the docked portal
         * render. Preserve the current unsaved Slate draft so the remounted
         * editor can pick up where the previous layout left off.
         */
        if (
            !message
            && Array.isArray(initialDraftValue)
            && initialDraftValue.length > 0
        ) {
            return JSON.parse(JSON.stringify(initialDraftValue)) as Descendant[];
        }

        const postContent = message;
        if (
            !postContent
            || (
                postContent
                && postContent.value.length === 0
            )
        ) {
            return INITIAL_EDITOR_VALUE;
        }

        let contentTree;
        try {
            contentTree = JSON.parse(postContent!.value);
        } catch (error) {
            if (currentSessionId) {
                recordUserAction({
                    test: isTestMode(),
                    type: USER_ACTION_TYPE.error,
                    sessionId: currentSessionId,
                    payload: {
                        // eslint-disable-next-line max-len
                        error: `There was a problem interpreting the annotation message. Please reach out to verascope support at: ${SUPPORT_EMAIL_ADDRESS}.`,
                    },
                });
            }
            // eslint-disable-next-line max-len
            throw Error(`There was a problem interpreting the annotation message. Please reach out to verascope support at: ${SUPPORT_EMAIL_ADDRESS}.`);
        }

        const tempEditor = EDITOR_PLUGINS
            .reduce((
                compositeEditor: Editor,
                withPlugin: (editor: Editor) => Editor,
            ) => withPlugin(compositeEditor), createEditor());
        tempEditor.children = contentTree;
        Editor.normalize(tempEditor, { force: true });
        return tempEditor.children;
    };
    const slateValue = useMemo(getSavedSlateValue, [initialDraftValue, message]);
    // Stores the slate editor value
    const [value, setValue] = useState(slateValue);
    const [xPos, setXPos] = useState<number | null>(left);
    const [yPos, setYPos] = useState<number | null>(top);
    const [mobileKeyboardOffset, setMobileKeyboardOffset] = useState<number>(0);
    // Allow Submit Message
    const [canSubmit, setCanSubmit] = useState(false);
    // Stores the position of the selection toolbar
    const [selectionToolbarPosition, setSelectionToolbarPosition] = useState<ICoord>({
        y: 24,
        x: -10,
    });
    // Stores whether we should rerender elements that have measuring logic
    // This occurs when we need to wait for rendering to complete, such as with images
    // in figures.
    const [rerenderMeasuringElements, setRerenderMeasuringElements] = useState<boolean>(false);
    // Stores whether audio is being recorded
    // Makes sure we record one audio note at a time
    const [isRecordingAudioNote, setIsRecordingAudioNote] = useState<boolean>(false);
    // Audio notes report their local draft/save state continuously so submit
    // gating no longer has to probe child nodes at click time.
    const [dirtyAudioNoteIds, setDirtyAudioNoteIds] = useState<Set<string>>(new Set());
    const [savingAudioNoteIds, setSavingAudioNoteIds] = useState<Set<string>>(new Set());
    // Stores the state of the editor modal
    const [modal, setModal] = useState<{
        visible: boolean,
        message: IModalMessage,
        proceedCallback?:() => void,
        rejectCallback?: () => void,
            }>({
                visible: false,
                message: {},
            });
    // Stores whether the stationary toolbar is visible
    const [stationaryToolbarIsActive, setStationaryToolbarIsActive] = useState<boolean>(false);
    // Schedules modification of stationaryToolbarIsActive
    const [showStationaryToolbar, setShowStationaryToolbar] = useState<boolean>(false);
    // Lets docked toolkits finish their exit animation before the shell collapses.
    const [stationaryToolbarIsClosing, setStationaryToolbarIsClosing] = useState<boolean>(false);
    // Indicates whether annotation editor is visible
    const [showAnnotationEditor, setShowAnnotationEditor] = useState<boolean>(readOnly || !!isCommentEditor);
    const previousShowAnnotationEditorRef = useRef<boolean>(readOnly || !!isCommentEditor);
    const appliedShowAnnotationEditorOverrideRef = useRef<boolean>(false);
    const pendingReaderAnnotationEditorOpenSyncRef = useRef<boolean>(false);
    const readerAnnotationEditorDeferredOpenSyncTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const readerAnnotationEditorOpenSyncTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const readerAnnotationEditorParentOpenStateRef = useRef<{
        showAnnotationEditorOverride: boolean,
        hasPostQuote: boolean,
    }>({
        showAnnotationEditorOverride: !!showAnnotationEditorOverride,
        hasPostQuote: !!postQuote,
    });
    const pendingReaderSelectionFocusQuoteIdRef = useRef<string | null>(null);
    const deferredReaderSelectionFocusRafRef = useRef<number | null>(null);
    const deferredReaderSelectionPositionRafRef = useRef<number | null>(null);
    // Stores whether emoji selector is exposed
    const [showEmojiSelector, setShowEmojiSelector] = useState<boolean>(false);
    // Stores the height of the stationary toolbar
    const [stationaryToolbarHeight, setStationaryToolbarHeight] = useState<number>(0);
    const dockAnnotationEditorToViewport = mobileViewportEligible
        && (
            showAnnotationEditor
            || !!postQuote
        );
    /**
     * Existing annotations reuse a mounted inline editor when read mode flips
     * into edit mode. That editor has no live ReaderView quote, so it must not
     * be treated like the transient highlight composer draft.
     */
    const preserveExistingAnnotationEditShell = (
        !readOnly
        && !isCommentEditor
        && !!editAnnotation
        && !!message
    );
    /**
     * Docked mobile snackbars need extra clearance so they render above the
     * annotation editor shell instead of sliding behind it.
     */
    const annotationEditorSnackbarBottomOffset = dockAnnotationEditorToViewport
        ? 11.25 * BODY_FONT_SIZE
        : undefined;

    const clearDeferredReaderSelectionFocus = useCallback((): void => {
        pendingReaderSelectionFocusQuoteIdRef.current = null;
        if (deferredReaderSelectionFocusRafRef.current != null) {
            window.cancelAnimationFrame(deferredReaderSelectionFocusRafRef.current);
            deferredReaderSelectionFocusRafRef.current = null;
        }
    }, []);
    const clearDeferredReaderSelectionPositionUpdate = useCallback((): void => {
        if (deferredReaderSelectionPositionRafRef.current != null) {
            window.cancelAnimationFrame(deferredReaderSelectionPositionRafRef.current);
            deferredReaderSelectionPositionRafRef.current = null;
        }
    }, []);
    /**
     * When the floating reader editor is already open, queue the next
     * selection-driven position write one frame later so the browser can animate
     * from the previously painted coordinates instead of collapsing both writes
     * into the same frame.
     */
    const updatePositionForReaderSelection = useCallback((
        recomputePlacement?: boolean,
        selectionPlacementHint?: IAnnotationEditorSelectionPlacementHint,
        animateFromVisiblePosition = false,
    ): void => {
        if (!updatePosition) {
            return;
        }

        clearDeferredReaderSelectionPositionUpdate();
        if (!animateFromVisiblePosition) {
            updatePosition(recomputePlacement, selectionPlacementHint);
            return;
        }

        deferredReaderSelectionPositionRafRef.current = window.requestAnimationFrame(() => {
            deferredReaderSelectionPositionRafRef.current = null;
            updatePosition(recomputePlacement, selectionPlacementHint);
        });
    }, [
        clearDeferredReaderSelectionPositionUpdate,
        updatePosition,
    ]);
    const shouldDeferReaderSelectionFocusUntilPostQuotePaint = deferReaderSelectionFocusUntilPostQuotePaint
        && !readOnly
        && !isCommentEditor;

    useEffect(() => {
        readerAnnotationEditorParentOpenStateRef.current = {
            showAnnotationEditorOverride: !!showAnnotationEditorOverride,
            hasPostQuote: !!postQuote,
        };
    }, [
        postQuote,
        showAnnotationEditorOverride,
    ]);

    const createAnnotationEditorSnackbar = (item: ISnackbarItem): ISnackbarItem => ({
        ...item,
        ...(typeof annotationEditorSnackbarBottomOffset === 'number'
            ? { bottomOffset: annotationEditorSnackbarBottomOffset }
            : {}),
    });
    /**
     * Small desktop windows still use the docked reader layout, but only
     * touch-first viewports need the stationary toolbar groups collapsed by
     * default. Keeping desktop narrow viewports unchanged preserves the denser
     * editing workflow there while reducing clutter on phones.
     */
    const touchViewportHasCoarsePointer = useMemo(() => (
        mobileViewportEligible
        && typeof document !== 'undefined'
        && detectTouchDevice(document)
    ), [mobileViewportEligible]);
    /**
     * Mobile annotations should open with the tool groups collapsed so the
     * editor body keeps as much vertical runway as possible above the keyboard.
     */
    const stationaryToolbarDefaultOpenGroups = useMemo(() => {
        const defaultGroupOpen = !touchViewportHasCoarsePointer;
        return new Map([
            [EDITOR_TOOLBAR_TOOL_GROUP.text, defaultGroupOpen],
            [EDITOR_TOOLBAR_TOOL_GROUP.layout, defaultGroupOpen],
            [EDITOR_TOOLBAR_TOOL_GROUP.media, defaultGroupOpen],
            [EDITOR_TOOLBAR_TOOL_GROUP.embeddings, defaultGroupOpen],
        ]);
    }, [touchViewportHasCoarsePointer]);
    const resolvedShowStationaryToolbar = showStationaryToolbar;
    /**
     * Touch-mobile readers keep the annotation editor mounted in a stable body
     * portal so the first highlight can open it without remounting the editor.
     * When the editor is still dormant, this flag lets the shell stay fixed and
     * out of normal document flow without enabling the active dock behavior.
     */
    const pinAnnotationEditorShellToViewport = mobileViewportEligible;
    // Flag to enable rapid creation of an audio note
    const [autoRecord, setAutoRecord] = useState<boolean>(false);
    // Upload dropzone files to storage
    // and create figure or audio note
    const onDropAccepted = useCallback((acceptedFiles: File[]) => {
        if (!readOnly && postId != null) {
            const uploadTasks: UploadTask[] = [];
            const mediaItems: IMediaItem[] = [];
            for (let i = 0; i < acceptedFiles.length; i += 1) {
                const file = acceptedFiles[i];
                let mediaType: MEDIA_TYPE;
                const mediaClass = file.type.split('/')[0];

                switch (mediaClass) {
                case 'audio':
                    mediaType = MEDIA_TYPE.audioNote;
                    break;
                default:
                    // All images will be mapped to image annotation because we
                    // don't expect sketches to be uploaded via this flow
                    mediaType = MEDIA_TYPE.image;
                    break;
                }

                const mediaId = uuidv4();
                const uniqueId = new ShortUniqueId({ length: 6 })(); // avoid file name collisions
                const mediaBucket = getMediaStorageBucket(mediaType);
                const fileName = file.name.toLowerCase().split('.')[0].replace(' ', '_');
                const fileExtension = mime.getExtension(file.type);
                if (fileExtension == null) {
                    setSnackbarData(createAnnotationEditorSnackbar({
                        visible: true,
                        duration: EXTENDED_SNACKBAR_VISIBLE_DURATION,
                        text: `Unable to determine file extension. Please reach out to verascope support at: ${SUPPORT_EMAIL_ADDRESS}.`,
                        icon: FileIcon,
                        hasFailure: true,
                    }));
                    return;
                }
                const storageEntity = isTestMode()
                    ? STORAGE_ENTITY.stagingAnnotations
                    : STORAGE_ENTITY.annotations;
                const filePath = `${storageEntity}/${mediaBucket}/${mediaId}/${fileName}-${uniqueId}.${fileExtension}`;
                const mediaItem: IMediaItem = {
                    id: mediaId,
                    userId: user!.id,
                    file,
                    filePath,
                    type: mediaType,
                    uploadProgress: 0,
                };
                mediaItems.push(mediaItem);
                addUploadingMedia(mediaItem);

                // make db entry of media
                // execute before file upload so upload cloud function has a place to write to
                createMedia({
                    test: isTestMode(),
                    postId,
                    mediaId,
                    type: mediaType,
                    storageEntity,
                    fileName: `${fileName}-${uniqueId}`,
                    fileExtension: fileExtension.toString(),
                });

                // upload file to cloud storage
                // url will be set by cloud function
                const uploadTask = uploadToCloudStorage(
                    file,
                    filePath,
                    user!.id,
                );
                uploadTasks.push(uploadTask);
            }

            uploadTasks.forEach((task: UploadTask, index: number) => {
                task.on(
                    'state_changed',
                    (snapshot: UploadTaskSnapshot) => {
                        // Observe state change events such as progress, pause, and resume
                        // Get task progress, including the number of bytes uploaded and the total number of bytes to be uploaded
                        const progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
                        setUploadingStateChangeEvent({
                            mediaItem: mediaItems[index],
                            snapshot,
                            progress,
                        });
                    },
                    (error: StorageError) => {
                        if (currentSessionId) {
                            recordUserAction({
                                test: isTestMode(),
                                type: USER_ACTION_TYPE.error,
                                sessionId: currentSessionId,
                                payload: {
                                    // eslint-disable-next-line max-len
                                    error: getStorageErrorMessage(error.code as STORAGE_ERROR_CODE),
                                },
                            });
                        }
                        throw Error(getStorageErrorMessage(error.code as STORAGE_ERROR_CODE));
                    },
                    () => {
                        setUploadingCompleteEvent({
                            mediaItem: mediaItems[index],
                            storageRef: task.snapshot.ref,
                        });
                    },
                );
            });
        }
    }, [
        readOnly,
        user,
        postId,
    ]);
    // Set up dropzone for images and audio
    const {
        getRootProps,
        getInputProps,
        isDragAccept,
        isDragActive,
        isDragReject,
    } = useDropzone({
        multiple: true,
        noClick: true,
        noKeyboard: true,
        accept: ['image/*', 'audio/*'],
        onDropAccepted,
    });
    const [uploadingMedia, setUploadingMedia] = useState<Map<string, IMediaItem>>(new Map());
    // Various media items are competing to write over this, so we don't independently
    // We track the progress within each media item not here
    const [uploadingStateChangeEvent, setUploadingStateChangeEvent] = useState<{
        mediaItem: IMediaItem,
        snapshot: UploadTaskSnapshot,
        progress: number,
    } | null>(null);
    // Various media items are competing to write over this, so we don't independently
    // We track the completion within each media item not here
    const [uploadingCompleteEvent, setUploadingCompleteEvent] = useState<{
        mediaItem: IMediaItem,
        storageRef: StorageReference,
    } | null>(null);
    const [dictationWords, setDictationWords] = useState<ITranscriptionWord[]>([]);
    const [currentDictationWordIndex, setCurrentDictationWordIndex] = useState<number | null>(null);
    const [cachedValue, setCachedValue] = useState< Descendant[] | null>(null);
    const lastSendTriggerTimestampRef = useRef<number>(0);
    const isMobileKeyboardOffsetTarget = useMemo(() => (
        typeof window !== 'undefined'
        && /iPhone|iPod|Android/i.test(window.navigator.userAgent)
    ), []);
    const normalizeDictationToken = useCallback((token: string): string => (
        token
            .toLowerCase()
            .replace(REMOVE_LEADING_AND_TRAILING_PUNCTUATION_REGEX, '')
            .replace(/[^a-z0-9]/g, '')
    ), []);
    const getTranscriptionTimeMs = useCallback((
        time?: {
            seconds?: string | number,
            nanos?: number,
        },
    ): number => {
        const secondsRaw = time?.seconds;
        let parsedSeconds = 0;
        if (typeof secondsRaw === 'string') {
            parsedSeconds = Number.parseInt(secondsRaw, 10);
        } else if (typeof secondsRaw === 'number') {
            parsedSeconds = secondsRaw;
        }
        const seconds = Number.isFinite(parsedSeconds) ? parsedSeconds : 0;
        const nanosRaw = typeof time?.nanos === 'number' ? time.nanos : 0;
        const nanos = Number.isFinite(nanosRaw) ? nanosRaw : 0;

        return (MILLISECONDS_IN_A_SECOND * seconds)
            + (MILLISECONDS_IN_A_SECOND * nanos) / NANOSECONDS_IN_A_SECOND;
    }, []);

    const editorDictationTokens = useMemo(() => {
        const tokens: IEditorDictationToken[] = [];
        const walkNodes = (nodes: Descendant[], pathPrefix: number[] = []): void => {
            nodes.forEach((childNode, childIndex) => {
                const path = [...pathPrefix, childIndex];
                if (Text.isText(childNode)) {
                    const textMatches = Array.from(childNode.text.matchAll(/\S+/g));
                    textMatches.forEach((textMatch) => {
                        if (typeof textMatch.index !== 'number') return;
                        const tokenText = textMatch[0];
                        const normalized = normalizeDictationToken(tokenText);
                        if (normalized.length === 0) return;
                        tokens.push({
                            normalized,
                            path,
                            startOffset: textMatch.index,
                            endOffset: textMatch.index + tokenText.length,
                        });
                    });
                    return;
                }

                if ('children' in childNode && Array.isArray(childNode.children)) {
                    walkNodes(childNode.children as Descendant[], path);
                }
            });
        };

        walkNodes(value);
        return tokens;
    }, [
        value,
        normalizeDictationToken,
    ]);

    const alignedTranscriptTokenIndices = useMemo(() => {
        if (dictationWords.length === 0 || editorDictationTokens.length === 0) {
            return [] as Array<number | null>;
        }

        const alignedIndices: Array<number | null> = [];
        const normalizedTranscriptWords = dictationWords.map((word) => (
            normalizeDictationToken(word.word || '')
        ));
        let cursor = 0;

        for (let transcriptIndex = 0; transcriptIndex < dictationWords.length; transcriptIndex += 1) {
            const normalizedTranscriptWord = normalizedTranscriptWords[transcriptIndex];
            if (normalizedTranscriptWord.length === 0) {
                alignedIndices.push(null);
            } else {
                let bestEditorIndex: number | null = null;
                let bestScore = Number.POSITIVE_INFINITY;
                const nextTranscriptWord = transcriptIndex + 1 < normalizedTranscriptWords.length
                    ? normalizedTranscriptWords[transcriptIndex + 1]
                    : '';

                for (let editorIndex = cursor; editorIndex < editorDictationTokens.length; editorIndex += 1) {
                    const editorToken = editorDictationTokens[editorIndex];
                    if (editorToken.normalized === normalizedTranscriptWord) {
                        let score = editorIndex - cursor;
                        if (
                            nextTranscriptWord.length > 0
                            && editorIndex + 1 < editorDictationTokens.length
                            && editorDictationTokens[editorIndex + 1].normalized === nextTranscriptWord
                        ) {
                            score -= 0.5;
                        }

                        if (score < bestScore) {
                            bestScore = score;
                            bestEditorIndex = editorIndex;
                        }

                        if (score <= 0) {
                            break;
                        }
                    }
                }

                if (bestEditorIndex == null && cursor < editorDictationTokens.length) {
                    let fallbackIndex: number | null = null;
                    // If the next transcript token has an exact anchor in editor text,
                    // backfill current token position relative to that anchor.
                    for (let lookaheadIndex = transcriptIndex + 1; lookaheadIndex < normalizedTranscriptWords.length; lookaheadIndex += 1) {
                        const lookaheadWord = normalizedTranscriptWords[lookaheadIndex];
                        if (lookaheadWord.length === 0) {
                            // Skip empty normalized tokens.
                            // eslint-disable-next-line no-continue
                            continue;
                        }

                        let anchorEditorIndex = -1;
                        for (let tokenIndex = cursor; tokenIndex < editorDictationTokens.length; tokenIndex += 1) {
                            if (editorDictationTokens[tokenIndex].normalized === lookaheadWord) {
                                anchorEditorIndex = tokenIndex;
                                break;
                            }
                        }
                        if (anchorEditorIndex !== -1) {
                            const transcriptGap = lookaheadIndex - transcriptIndex;
                            const proposedIndex = Math.max(cursor, anchorEditorIndex - transcriptGap);
                            fallbackIndex = Math.min(proposedIndex, editorDictationTokens.length - 1);
                            break;
                        }
                    }

                    if (fallbackIndex == null) {
                        fallbackIndex = cursor;
                    }
                    bestEditorIndex = fallbackIndex;
                }

                alignedIndices.push(bestEditorIndex);
                if (typeof bestEditorIndex === 'number') {
                    cursor = bestEditorIndex + 1;
                }
            }
        }

        return alignedIndices;
    }, [
        dictationWords,
        editorDictationTokens,
        normalizeDictationToken,
    ]);

    const currentDictationDecorationTarget = useMemo(() => {
        if (
            !transcript
            || dictationWords.length === 0
            || currentDictationWordIndex == null
            || currentDictationWordIndex < 0
            || currentDictationWordIndex >= alignedTranscriptTokenIndices.length
        ) {
            return null;
        }

        const activeEditorTokenIndex = alignedTranscriptTokenIndices[currentDictationWordIndex];
        if (
            typeof activeEditorTokenIndex !== 'number'
            || activeEditorTokenIndex < 0
            || activeEditorTokenIndex >= editorDictationTokens.length
        ) {
            return null;
        }

        return editorDictationTokens[activeEditorTokenIndex];
    }, [
        transcript,
        dictationWords.length,
        currentDictationWordIndex,
        alignedTranscriptTokenIndices,
        editorDictationTokens,
    ]);

    const editor = useMemo(() => EDITOR_PLUGINS
        .reduce((compositeEditor, withPlugin) => withPlugin(compositeEditor), createEditor()), []);

    // ===== Animation Constants =====

    const STATIONARY_TOOL_BAR_TRANSITION_DURATION = 200;
    const EMOJI_SELECTOR_TRANSITION_DURATION = 150;
    const UPLOAD_TRANSITION_DELAY = 200;
    const EDITOR_TRANSITION_DURATION = 150;
    /**
     * Docked touch-mobile editors should expose their stationary toolbar shell
     * immediately. Letting the shell animate its height after the editor opens
     * makes the footer look like it jitters during the first scroll frames even
     * when the viewport root itself is already locked in place.
     */
    const stationaryToolbarTransitionDuration = dockAnnotationEditorToViewport
        ? 0
        : STATIONARY_TOOL_BAR_TRANSITION_DURATION;
    /**
     * Closing should always wait for the full stationary-toolbar group
     * lifecycle to finish before the shell collapses, including on docked
     * mobile layouts where the shell itself no longer animates its height.
     */
    const stationaryToolbarCloseDelay = stationaryToolbarLifecycleDelay;

    // ===== Hotkeys =====

    const handleOnKeyDown = useMemo(() => HandleOnKeyDown({
        tabCallback: () => {
            ListHelpers.indentSelectionLists(editor);
            LineHelpers.indentLineElements(editor);
        },
        shiftTabCallback: () => {
            ListHelpers.unindentSelectionLists(editor);
            LineHelpers.unindentLineElements(editor);
        },
        modBCallback: () => EmphasizerHelpers.toggleBold(editor),
        modICallback: () => EmphasizerHelpers.toggleItalicize(editor),
        modUCallback: () => EmphasizerHelpers.toggleUnderline(editor),
        modACallback: () => timeoutSelectAllText(),
        modShiftSCallback: () => EmphasizerHelpers.toggleStrikethrough(editor),
    }), []);

    // ===== Decorations =====

    const decorate = useCallback(([node, path]: NodeEntry) => {
        const decorations: IEditorDecoration[] = [];

        if (
            currentDictationDecorationTarget
            && (
                dictationPlaying
                || (timeElapsed && timeElapsed > 0)
            ) && Text.isText(node)
            && Path.equals(path, currentDictationDecorationTarget.path)
        ) {
            const decoration = {
                anchor: {
                    path,
                    offset: currentDictationDecorationTarget.startOffset,
                },
                focus: {
                    path,
                    offset: currentDictationDecorationTarget.endOffset,
                },
                dictationHighlight: true,
                highlightColor: highlightColor || themeObj.verascopeColor.purple400,
            };
            decorations.push(decoration);
        }
        return decorations;
    }, [
        currentDictationDecorationTarget,
        timeElapsed,
        dictationPlaying,
    ]);

    /**
     * The annotation toolbar can pin its URL row above the editor shell. Those
     * interactions still belong to the draft and should not count as
     * click-away dismissal.
     */
    const eventTargetsAnnotationToolbarLinkInput = (event: Event): boolean => {
        if (!event.composedPath) {
            return false;
        }

        return event.composedPath().some((ele: EventTarget) => {
            const target = ele as HTMLElement;

            return !!target.dataset
                && target.dataset.annotationEditorToolbarLinkInput === 'true';
        });
    };

    /**
     * Annotation-owned modals render in a portal outside the editor shell, so
     * their overlay and buttons need an explicit marker to stay inside the
     * editor's interaction boundary.
     */
    const eventTargetsAnnotationEditorModal = (event: Event): boolean => {
        if (!event.composedPath) {
            return false;
        }

        return event.composedPath().some((ele: EventTarget) => {
            const target = ele as HTMLElement;

            return !!target.dataset
                && target.dataset.annotationEditorModal === 'true';
        });
    };

    /**
     * The stationary toolkit shell can remain mounted briefly while its hidden
     * closing state drains. Once the shell content is collapsed, clicks on that
     * leftover box should dismiss the annotation just like any other outside
     * click instead of being treated as inside-editor interactions.
     */
    const eventTargetsCollapsedAnnotationToolbarShell = (event: Event): boolean => {
        if (!event.composedPath) {
            return false;
        }

        return event.composedPath().some((ele: EventTarget) => {
            const target = ele as HTMLElement;

            return !!target.dataset
                && target.dataset.annotationEditorStationaryToolbarShell === 'collapsed';
        });
    };

    /**
     * Outside-click dismissal should ignore browser selections that still live
     * inside the draft itself. Those selections are editor-internal state, not
     * a signal that the reader is creating or preserving a post highlight.
     */
    const selectionTargetsAnnotationEditor = (
        selection: globalThis.Selection | null,
    ): boolean => {
        if (!selection || !containerRef.current) {
            return false;
        }

        return (
            (!!selection.anchorNode && containerRef.current.contains(selection.anchorNode))
            || (!!selection.focusNode && containerRef.current.contains(selection.focusNode))
        );
    };

    /**
     * Treat the hidden stationary shell as outside the annotation editor so
     * click-away logic matches the visible editor boundary.
     */
    const eventTargetsOutsideAnnotationEditor = (event: Event): boolean => {
        if (!containerRef.current || !event.composedPath) {
            return false;
        }

        return !event.composedPath().includes(containerRef.current)
            || eventTargetsCollapsedAnnotationToolbarShell(event);
    };

    /**
     * Hides the active annotation draft and clears the associated quote so the
     * reader returns to its neutral selection state.
     */
    const closeAnnotationEditor = (
        reason: ANNOTATION_EDITOR_CLOSE_REASON = ANNOTATION_EDITOR_CLOSE_REASON.unspecified,
    ): void => {
        logAnnotationEditDebug('annotation-editor-close-request', {
            reason,
            showAnnotationEditor,
            showAnnotationEditorOverride,
            hasPostQuote: !!postQuote,
            pendingReaderAnnotationEditorOpenSync: pendingReaderAnnotationEditorOpenSyncRef.current,
            readerAnnotationEditorParentOpenState: {
                ...readerAnnotationEditorParentOpenStateRef.current,
            },
        });
        pendingReaderAnnotationEditorOpenSyncRef.current = false;
        if (desktopSelectionCollapseRecheckTimeoutRef.current) {
            clearTimeout(desktopSelectionCollapseRecheckTimeoutRef.current);
            desktopSelectionCollapseRecheckTimeoutRef.current = null;
        }
        if (readerAnnotationEditorOpenSyncTimeoutRef.current) {
            clearTimeout(readerAnnotationEditorOpenSyncTimeoutRef.current);
            readerAnnotationEditorOpenSyncTimeoutRef.current = null;
        }
        if (readerAnnotationEditorDeferredOpenSyncTimeoutRef.current) {
            clearTimeout(readerAnnotationEditorDeferredOpenSyncTimeoutRef.current);
            readerAnnotationEditorDeferredOpenSyncTimeoutRef.current = null;
        }
        clearDelayMoveCursorToEndOfDocument();
        clearDeferredReaderSelectionFocus();
        clearDeferredReaderSelectionPositionUpdate();
        batchUpdates(() => {
            setShowAnnotationEditor(false);
            if (handleShowAnnotationEditor) handleShowAnnotationEditor(false);
            if (postQuote && setPostQuote) {
                setPostQuote(null);
            }
        });
    };

    /**
     * Toolbar URL-row actions can temporarily collapse the reader selection
     * while still belonging to the current annotation draft. Keep the draft
     * mounted through that short follow-up event burst.
     */
    const requestAnnotationEditorDismissalSuppression = useCallback((): void => {
        suppressAnnotationEditorDismissUntilRef.current = Date.now() + 250;
    }, [
        clearDeferredReaderSelectionFocus,
        clearDeferredReaderSelectionPositionUpdate,
    ]);

    /**
     * Let the local editor shell paint before ReaderView recomputes the active
     * quote decorations. Keeping the parent sync one task later preserves the
     * correctness fix without making every highlight wait on the full reader
     * rerender first.
     */
    const scheduleReaderAnnotationEditorOpenStateSync = useCallback((nextQuote: IAnnotationQuote): void => {
        if (readerAnnotationEditorDeferredOpenSyncTimeoutRef.current) {
            clearTimeout(readerAnnotationEditorDeferredOpenSyncTimeoutRef.current);
        }

        readerAnnotationEditorDeferredOpenSyncTimeoutRef.current = setTimeout(() => {
            readerAnnotationEditorDeferredOpenSyncTimeoutRef.current = null;
            batchUpdates(() => {
                if (handleShowAnnotationEditor) handleShowAnnotationEditor(true);
                if (setPostQuote) setPostQuote(nextQuote);
            });
        }, 0);
    }, [
        handleShowAnnotationEditor,
        setPostQuote,
    ]);

    /**
     * Desktop readers can leave the previous DOM selection painted through the
     * mouseup that should dismiss the annotation editor, then collapse it
     * immediately afterward. Recheck once the browser settles so dismissal no
     * longer depends on the trailing native click event.
     */
    const scheduleDesktopSelectionCollapseRecheck = useCallback((event: Event): void => {
        if (
            !postId
            || readOnly
            || isCommentEditor
            || !showAnnotationEditor
            || !viewportDimensions
            || viewportDimensions.width < MEDIA_QUERY_SIZE.medium.min
            || !!resolveTouchEvent(event)
        ) {
            return;
        }

        const targetEditor = event.composedPath().find((ele: EventTarget) => {
            const target = ele as HTMLElement;
            if (target.dataset) {
                return target.dataset.id === postId;
            }

            return false;
        });
        if (!targetEditor || !eventTargetsOutsideAnnotationEditor(event)) {
            return;
        }

        if (desktopSelectionCollapseRecheckTimeoutRef.current) {
            clearTimeout(desktopSelectionCollapseRecheckTimeoutRef.current);
        }

        desktopSelectionCollapseRecheckTimeoutRef.current = setTimeout(() => {
            desktopSelectionCollapseRecheckTimeoutRef.current = null;

            const nativeSelection = window.getSelection();
            const nativeSelectionText = nativeSelection?.toString().trim() || '';
            const hasVisibleSelection = !!nativeSelection
                && !nativeSelection.isCollapsed
                && nativeSelectionText.length > 0;
            const selectionInsideAnnotationEditor = selectionTargetsAnnotationEditor(nativeSelection);

            logAnnotationEditDebug('annotation-editor-post-mouseup-collapse-recheck', {
                hasVisibleSelection,
                nativeSelection: resolveNativeSelectionDebugPayload(nativeSelection),
                selectionInsideAnnotationEditor,
                showAnnotationEditor,
            });

            if (hasVisibleSelection || selectionInsideAnnotationEditor) {
                return;
            }

            closeAnnotationEditor(ANNOTATION_EDITOR_CLOSE_REASON.postMouseUpSelectionCollapsed);
        }, 0);
    }, [
        closeAnnotationEditor,
        isCommentEditor,
        postId,
        readOnly,
        showAnnotationEditor,
        viewportDimensions,
    ]);

    const shouldSuppressAnnotationEditorDismissal = (): boolean => (
        Date.now() < suppressAnnotationEditorDismissUntilRef.current
    );

    /**
     * Read-along playback updates post decorations continuously, so a fresh
     * highlight can feel unstable while audio is still running. Pause the
     * active note once we have a valid quote to keep annotation authoring
     * predictable.
     */
    const pauseActiveReadOnlyAudioPlayback = useCallback((): void => {
        if (!pauseReadOnlyAudioPlayback) {
            return;
        }

        pauseReadOnlyAudioPlayback();
    }, [
        pauseReadOnlyAudioPlayback,
    ]);

    /**
     * Editor-owned confirmation modals render in a portal outside the editor
     * shell, so their button presses would otherwise look like click-away
     * dismissal. Keep the draft mounted while those modal interactions run.
     */
    const annotationEditorDismissalBlocked = (event?: Event): boolean => (
        modal.visible
        || shouldSuppressAnnotationEditorDismissal()
        || (!!event && (
            eventTargetsAnnotationEditorModal(event)
            || eventTargetsAnnotationToolbarLinkInput(event)
        ))
    );

    const handleHighlightPostText = (e: Event, keyup: boolean): IHandleHighlightPostTextResult => {
        if (annotationEditorDismissalBlocked(e)) {
            return {
                nativeSelection: window.getSelection(),
                highlightHandled: false,
            };
        }

        const targetEditor = e.composedPath().find((ele: EventTarget) => {
            const target = (ele as HTMLElement);
            if (target.dataset) {
                return target.dataset.id === postId;
            }

            return false;
        });
        const touchEventRepresentsTap = (): boolean => {
            const touchEvent = resolveTouchEvent(e);
            if (!touchEvent) {
                return false;
            }

            const lastTouchGesture = touchGestureRef.current;
            const touch = touchEvent.changedTouches[0];
            if (!lastTouchGesture || !touch) {
                return false;
            }

            return !lastTouchGesture.moved
                && Math.abs(touch.clientX - lastTouchGesture.x) <= TOUCH_TAP_MAX_MOVEMENT_PX
                && Math.abs(touch.clientY - lastTouchGesture.y) <= TOUCH_TAP_MAX_MOVEMENT_PX;
        };
        const interactionTargetsPostEditor = !!targetEditor;
        const outsideAnnotationEditor = eventTargetsOutsideAnnotationEditor(e);
        const pointerIsTouch = !!resolveTouchEvent(e);
        /**
         * A genuine tap-away should dismiss immediately instead of depending on
         * whichever DOM selection the browser happens to preserve through the
         * event. Drag-selection still flows through the selection resolver
         * below because those gestures are not taps.
         */
        const outsideInteractionRepresentsTap = !keyup
            && outsideAnnotationEditor
            && !interactionTargetsPostEditor
            && pointerIsTouch
            && touchEventRepresentsTap();
        /**
         * Mouse-driven outside clicks should dismiss immediately regardless of
         * viewport size. The tap/movement guard is only necessary for touch
         * paths, where scroll gestures and taps share the same event family.
         */
        const dismissFromOutsideInteraction = outsideAnnotationEditor && !interactionTargetsPostEditor && (
            !pointerIsTouch
            || outsideInteractionRepresentsTap
        );

        if (
            currentZoomLevel
            && currentZoomLevel.level === ZOOM_LEVEL.five
        ) {
            const nativeSelection = window.getSelection();
            const nativeSelectionText = nativeSelection?.toString().trim() || '';
            const hasVisibleSelection = !!nativeSelection
                && !nativeSelection.isCollapsed
                && nativeSelectionText.length > 0;
            const showEditor = hasVisibleSelection;
            const selectionInsideAnnotationEditor = selectionTargetsAnnotationEditor(nativeSelection);
            const selectionCheckpoint = keyup ? 'keyup' : 'pointer-up';
            const pointerGestureMoved = !!mouseGestureRef.current?.moved
                || !!touchGestureRef.current?.moved;
            const touchGestureMoved = !!touchGestureRef.current?.moved;

            if (
                showAnnotationEditor
                && outsideInteractionRepresentsTap
            ) {
                logAnnotationEditDebug('annotation-read-highlight-selection-decision', {
                    checkpoint: selectionCheckpoint,
                    decision: 'dismiss-outside-tap',
                    nativeSelection: resolveNativeSelectionDebugPayload(nativeSelection),
                    showAnnotationEditor,
                });
                closeAnnotationEditor(ANNOTATION_EDITOR_CLOSE_REASON.highlightDismissOutsideTap);
                return {
                    nativeSelection,
                    highlightHandled: false,
                };
            }

            if (
                !keyup
                && pointerGestureMoved
                && !hasVisibleSelection
            ) {
                logReadHighlightSelectionDebug({
                    checkpoint: selectionCheckpoint,
                    event: 'annotation-read-highlight-selection-resolution',
                    nativeSelection,
                    postQuote,
                    reason: 'selection-collapsed-before-resolution',
                    showAnnotationEditor,
                    cachedSelection: postEditor?.selection as Selection | null,
                    derivedSelection: null,
                    derivedSelectionMatchMode: 'none',
                    resolvedSelection: null,
                    resolvedSelectionSource: 'none',
                    selectionInsideAnnotationEditor,
                    touchGestureMoved,
                });
            }

            // If text selection and Annotation Editor not visible already
            // Show Annotation Editor
            if (
                targetEditor
                && hasVisibleSelection
                && showEditor
                && updatePosition
                && postEditor
                && setPostQuote
            ) {
                const resolvedHighlightSelection = resolveReadHighlightSelection({
                    cachedSelection: postEditor.selection as Selection | null,
                    editor: postEditor,
                    nativeSelection,
                });
                const {
                    cachedSelection,
                    derivedSelection,
                    derivedSelectionMatchMode,
                    resolvedSelection,
                    resolvedSelectionSource,
                } = resolvedHighlightSelection;

                logReadHighlightSelectionDebug({
                    checkpoint: selectionCheckpoint,
                    event: 'annotation-read-highlight-selection-resolution',
                    nativeSelection,
                    postQuote,
                    reason: resolvedSelection
                        ? 'resolved-selection'
                        : 'unresolved-selection',
                    showAnnotationEditor,
                    dismissFromOutsideInteraction,
                    cachedSelection,
                    derivedSelection,
                    derivedSelectionMatchMode,
                    resolvedSelection,
                    resolvedSelectionSource,
                    selectionInsideAnnotationEditor,
                    touchGestureMoved,
                });

                if (!resolvedSelection) {
                    if (
                        showAnnotationEditor
                        && selectionInsideAnnotationEditor
                        && dismissFromOutsideInteraction
                    ) {
                        logReadHighlightSelectionDebug({
                            checkpoint: selectionCheckpoint,
                            event: 'annotation-read-highlight-selection-decision',
                            nativeSelection,
                            postQuote,
                            reason: 'dismiss-selection-inside-annotation-editor',
                            showAnnotationEditor,
                            dismissFromOutsideInteraction,
                            cachedSelection,
                            derivedSelection,
                            derivedSelectionMatchMode,
                            resolvedSelection,
                            resolvedSelectionSource,
                            selectionInsideAnnotationEditor,
                            touchGestureMoved,
                        });
                        closeAnnotationEditor(ANNOTATION_EDITOR_CLOSE_REASON.highlightDismissSelectionInsideAnnotationEditor);
                        return {
                            nativeSelection,
                            highlightHandled: false,
                        };
                    }

                    if (
                        showAnnotationEditor
                        && dismissFromOutsideInteraction
                        && !!postQuote
                        && nativeSelectionText === postQuote.texts[0]?.trim()
                    ) {
                        logReadHighlightSelectionDebug({
                            checkpoint: selectionCheckpoint,
                            event: 'annotation-read-highlight-selection-decision',
                            nativeSelection,
                            postQuote,
                            reason: 'dismiss-selection-matches-visible-quote-text',
                            showAnnotationEditor,
                            dismissFromOutsideInteraction,
                            cachedSelection,
                            derivedSelection,
                            derivedSelectionMatchMode,
                            resolvedSelection,
                            resolvedSelectionSource,
                            selectionInsideAnnotationEditor,
                            touchGestureMoved,
                        });
                        closeAnnotationEditor(ANNOTATION_EDITOR_CLOSE_REASON.highlightDismissSelectionMatchesVisibleQuoteText);
                    }

                    logReadHighlightSelectionDebug({
                        checkpoint: selectionCheckpoint,
                        event: 'annotation-read-highlight-selection-decision',
                        nativeSelection,
                        postQuote,
                        reason: 'ignore-unresolved-selection',
                        showAnnotationEditor,
                        dismissFromOutsideInteraction,
                        cachedSelection,
                        derivedSelection,
                        derivedSelectionMatchMode,
                        resolvedSelection,
                        resolvedSelectionSource,
                        selectionInsideAnnotationEditor,
                        touchGestureMoved,
                    });
                    return {
                        nativeSelection,
                        highlightHandled: false,
                    };
                }
                const { selection, text: selectedText } = resolvedSelection;
                const selectionMatchesVisibleHighlight = annotationQuoteMatchesSelection({
                    quote: postQuote,
                    selection,
                    text: selectedText,
                });

                if (
                    showAnnotationEditor
                    && selectionMatchesVisibleHighlight
                    && dismissFromOutsideInteraction
                ) {
                    logReadHighlightSelectionDebug({
                        checkpoint: selectionCheckpoint,
                        event: 'annotation-read-highlight-selection-decision',
                        nativeSelection,
                        postQuote,
                        reason: 'dismiss-matching-visible-highlight',
                        showAnnotationEditor,
                        dismissFromOutsideInteraction,
                        cachedSelection,
                        derivedSelection,
                        derivedSelectionMatchMode,
                        resolvedSelection,
                        resolvedSelectionSource,
                        selectionInsideAnnotationEditor,
                        selectionMatchesVisibleHighlight,
                        touchGestureMoved,
                    });
                    closeAnnotationEditor(ANNOTATION_EDITOR_CLOSE_REASON.highlightDismissMatchingVisibleHighlight);
                    return {
                        nativeSelection,
                        highlightHandled: false,
                    };
                }

                if (
                    showAnnotationEditor
                    && touchGestureMoved
                    && selectionMatchesVisibleHighlight
                ) {
                    logReadHighlightSelectionDebug({
                        checkpoint: selectionCheckpoint,
                        event: 'annotation-read-highlight-selection-decision',
                        nativeSelection,
                        postQuote,
                        reason: 'ignore-touch-duplicate-highlight',
                        showAnnotationEditor,
                        cachedSelection,
                        derivedSelection,
                        derivedSelectionMatchMode,
                        resolvedSelection,
                        resolvedSelectionSource,
                        selectionInsideAnnotationEditor,
                        selectionMatchesVisibleHighlight,
                        touchGestureMoved,
                    });
                    return {
                        nativeSelection,
                        highlightHandled: false,
                    };
                }

                if (
                    !keyup
                    && showAnnotationEditor
                    && interactionTargetsPostEditor
                    && !pointerGestureMoved
                    && selectionMatchesVisibleHighlight
                ) {
                    logReadHighlightSelectionDebug({
                        checkpoint: selectionCheckpoint,
                        event: 'annotation-read-highlight-selection-decision',
                        nativeSelection,
                        postQuote,
                        reason: 'defer-post-mouseup-collapse-recheck',
                        showAnnotationEditor,
                        cachedSelection,
                        derivedSelection,
                        derivedSelectionMatchMode,
                        resolvedSelection,
                        resolvedSelectionSource,
                        selectionInsideAnnotationEditor,
                        selectionMatchesVisibleHighlight,
                        touchGestureMoved,
                    });
                    return {
                        nativeSelection,
                        highlightHandled: false,
                    };
                }

                const selectionPlacementHint = resolveAnnotationEditorSelectionPlacementHint({
                    editor: postEditor,
                    selection,
                });
                const replacingVisibleHighlight = showAnnotationEditor && !!postQuote;
                const nextQuote = buildAnnotationQuote({
                    editor: postEditor,
                    id: uuidv4(),
                    timestamp: Date.now(),
                    selections: [selection],
                    texts: [selectedText],
                });
                pauseActiveReadOnlyAudioPlayback();

                /**
                 * Detached annotation repair only needs the next anchored
                 * selection. Skip opening the composer and hand the quote back
                 * to ReaderView immediately.
                 */
                if (captureQuoteWithoutOpeningEditor) {
                    logReadHighlightSelectionDebug({
                        checkpoint: selectionCheckpoint,
                        event: 'annotation-read-highlight-selection-decision',
                        nativeSelection,
                        postQuote,
                        reason: 'capture-quote-without-opening-editor',
                        showAnnotationEditor,
                        captureQuoteWithoutOpeningEditor,
                        cachedSelection,
                        derivedSelection,
                        derivedSelectionMatchMode,
                        resolvedSelection,
                        resolvedSelectionSource,
                        selectionInsideAnnotationEditor,
                        selectionMatchesVisibleHighlight,
                        selectionPlacementHint,
                        touchGestureMoved,
                    });
                    setPostQuote(nextQuote);
                    clearDelayMoveCursorToEndOfDocument();
                    moveCursorToEndOfDocument();
                    return {
                        nativeSelection,
                        highlightHandled: true,
                    };
                }

                pendingReaderAnnotationEditorOpenSyncRef.current = true;
                if (desktopSelectionCollapseRecheckTimeoutRef.current) {
                    clearTimeout(desktopSelectionCollapseRecheckTimeoutRef.current);
                    desktopSelectionCollapseRecheckTimeoutRef.current = null;
                }
                if (readerAnnotationEditorDeferredOpenSyncTimeoutRef.current) {
                    clearTimeout(readerAnnotationEditorDeferredOpenSyncTimeoutRef.current);
                    readerAnnotationEditorDeferredOpenSyncTimeoutRef.current = null;
                }
                if (readerAnnotationEditorOpenSyncTimeoutRef.current) {
                    clearTimeout(readerAnnotationEditorOpenSyncTimeoutRef.current);
                }
                readerAnnotationEditorOpenSyncTimeoutRef.current = setTimeout(() => {
                    pendingReaderAnnotationEditorOpenSyncRef.current = false;
                    readerAnnotationEditorOpenSyncTimeoutRef.current = null;
                    if (
                        !readerAnnotationEditorParentOpenStateRef.current.showAnnotationEditorOverride
                        || !readerAnnotationEditorParentOpenStateRef.current.hasPostQuote
                    ) {
                        logAnnotationEditDebug('annotation-editor-open-sync-timeout', {
                            showAnnotationEditor,
                            showAnnotationEditorOverride,
                            hasPostQuote: !!postQuote,
                            readerAnnotationEditorParentOpenState: {
                                ...readerAnnotationEditorParentOpenStateRef.current,
                            },
                        });
                        setShowAnnotationEditor(false);
                    }
                }, READER_ANNOTATION_OPEN_SYNC_TIMEOUT_MS);

                logReadHighlightSelectionDebug({
                    checkpoint: selectionCheckpoint,
                    event: 'annotation-read-highlight-selection-decision',
                    nativeSelection,
                    postQuote,
                    reason: 'open-editor-for-highlight',
                    showAnnotationEditor,
                    cachedSelection,
                    derivedSelection,
                    derivedSelectionMatchMode,
                    resolvedSelection,
                    resolvedSelectionSource,
                    selectionInsideAnnotationEditor,
                    selectionMatchesVisibleHighlight,
                    selectionPlacementHint,
                    touchGestureMoved,
                });
                flushSync(() => {
                    setShowAnnotationEditor(true);
                    if (
                        viewportDimensions
                        && viewportDimensions.width < MEDIA_QUERY_SIZE.medium.min
                        && !touchViewportHasCoarsePointer
                    ) setStationaryToolbarIsActive(true);
                });
                updatePositionForReaderSelection(
                    true,
                    selectionPlacementHint,
                    replacingVisibleHighlight && shouldDeferReaderSelectionFocusUntilPostQuotePaint,
                );
                scheduleReaderAnnotationEditorOpenStateSync(nextQuote);
                clearDelayMoveCursorToEndOfDocument();
                if (shouldDeferReaderSelectionFocusUntilPostQuotePaint) {
                    clearDeferredReaderSelectionFocus();
                    pendingReaderSelectionFocusQuoteIdRef.current = nextQuote.id;
                } else if (replacingVisibleHighlight) {
                    delayMoveCursorToEndOfDocument();
                } else {
                    moveCursorToEndOfDocument();
                }
                return {
                    nativeSelection,
                    highlightHandled: true,
                };
            }

            if (
                !keyup
                && showAnnotationEditor
                && dismissFromOutsideInteraction
            ) {
                logAnnotationEditDebug('annotation-read-highlight-selection-decision', {
                    checkpoint: selectionCheckpoint,
                    decision: 'dismiss-outside-interaction-without-selection',
                    nativeSelection: resolveNativeSelectionDebugPayload(nativeSelection),
                    showAnnotationEditor,
                });
                closeAnnotationEditor(ANNOTATION_EDITOR_CLOSE_REASON.highlightDismissOutsideInteractionWithoutSelection);
                return {
                    nativeSelection,
                    highlightHandled: false,
                };
            }

            if (
                keyup
                && nativeSelection?.isCollapsed
                && document.activeElement !== containerRef.current
                && !showEditor
                && !postQuote
                && showEditor !== showAnnotationEditor
            ) {
                logAnnotationEditDebug('annotation-read-highlight-selection-decision', {
                    checkpoint: selectionCheckpoint,
                    decision: 'dismiss-collapsed-keyup',
                    nativeSelection: resolveNativeSelectionDebugPayload(nativeSelection),
                    showAnnotationEditor,
                });
                closeAnnotationEditor(ANNOTATION_EDITOR_CLOSE_REASON.highlightDismissCollapsedKeyup);
                return {
                    nativeSelection,
                    highlightHandled: false,
                };
            }

            return {
                nativeSelection,
                highlightHandled: false,
            };
        }

        return {
            nativeSelection: null,
            highlightHandled: false,
        };
    };

    // ===== Side Effects =====

    // Set reference to editor to parent
    useEffect(() => {
        if (setEditor) setEditor(editor);
    }, []);

    useEffect(() => {
        if (containerRef.current && setRef) {
            setRef(containerRef.current);
        }
    }, [containerRef.current]);

    // update position of editor
    useEffect(() => {
        if (top !== yPos) {
            setYPos(top);
        }

        if (left !== xPos) {
            setXPos(left);
        }
    }, [
        top,
        left,
    ]);

    // Update to annotation
    useEffect(() => {
        if (message) {
            const updatedValue = getSavedSlateValue();
            setValue(updatedValue);
        }
    }, [
        message,
    ]);

    // Set Slate Editor Reference
    // and focus editor
    useEffect(() => {
        // Set Selection so cursor can be focused
        if (shouldFocus) {
            Transforms.select(editor, { path: [0, 0], offset: 0 });
        }
    }, []);

    useLayoutEffect(() => {
        const editorJustOpened = showAnnotationEditor
            && !previousShowAnnotationEditorRef.current;

        previousShowAnnotationEditorRef.current = showAnnotationEditor;

        if (
            !editorJustOpened
            || !shouldFocus
            || !!pendingReaderSelectionFocusQuoteIdRef.current
        ) {
            return;
        }

        /**
         * The touch-mobile annotation editor shell stays mounted between
         * highlights, so first-open focus can no longer rely on mount-time
         * Slate setup. Re-focus the live editor as soon as it becomes visible so
         * the keyboard opens on the first highlight and the dock logic sees a
         * focused editor immediately.
         */
        ReactEditor.focus(editor);
    }, [
        editor,
        dockAnnotationEditorToViewport,
        shouldFocus,
        showAnnotationEditor,
    ]);

    useEffect(() => {
        if (showAnnotationEditor) {
            appliedShowAnnotationEditorOverrideRef.current = true;
        }
    }, [showAnnotationEditor]);

    useEffect(() => {
        const cleanup = (): void => {
            if (inputClickClip.current) {
                inputClickClip.current.pause();
                inputClickClip.current.remove();
                inputClickClip.current = null;
            }
        };

        return cleanup;
    }, []);

    // Redetermine if we can submit message
    // Called when there is a change to Slate value
    useEffect(() => {
        // Update Can Submit Status
        updateCanSubmit(value);
    }, [value]);

    useEffect(() => {
        if (readOnly || isCommentEditor || !persistDraftValue) {
            return;
        }

        persistDraftValue(value);
    }, [
        isCommentEditor,
        persistDraftValue,
        readOnly,
        value,
    ]);

    // Update Positions of Toolbars on Resize
    useEventListener(
        'resize',
        () => {
            // Recompute height of stationary toolbar
            // Recompute height of stationary toolbar
            clearDelayComputeStationaryToolbarHeight();
            delayComputeStationaryToolbarHeight();
        },
    );

    useEffect(() => {
        if (transcript) {
            const transcriptWords = Array.isArray(transcript.data)
                ? transcript.data.reduce((words: any[], phrase: any) => (
                    [
                        ...words,
                        ...(Array.isArray(phrase?.alternatives?.[0]?.words) ? phrase.alternatives[0].words : []),
                    ]
                ), [])
                : [];
            const words: ITranscriptionWord[] = [];
            for (let i = 0; i < transcriptWords.length; i += 1) {
                const word = transcriptWords[i] as ITranscriptionWord;
                words.push(word);
            }

            setDictationWords([
                ...words,
            ]);
        } else {
            setDictationWords([]);
        }
    }, [
        transcript,
    ]);

    // Find and store current dictation word index
    useEffect(() => {
        if (dictationWords.length === 0 || typeof timeElapsed !== 'number') {
            if (currentDictationWordIndex !== null) setCurrentDictationWordIndex(null);
            return;
        }

        const currentWordIndex = binarySearchIndex(
            dictationWords,
            (word: ITranscriptionWord): boolean => getTranscriptionTimeMs(word.endTime) < timeElapsed,
            (word: ITranscriptionWord): boolean => getTranscriptionTimeMs(word.startTime) > timeElapsed,
        );

        if (typeof currentWordIndex === 'number') {
            if (currentDictationWordIndex !== currentWordIndex) {
                setCurrentDictationWordIndex(currentWordIndex);
            }
        } else if (currentDictationWordIndex !== null) {
            setCurrentDictationWordIndex(null);
        }
    }, [
        timeElapsed,
        dictationWords,
        currentDictationWordIndex,
        getTranscriptionTimeMs,
    ]);

    // Delay show stationaryToolbar open
    // Waits for stationary toolbar container to animate in
    const {
        start: delayShowStationaryToolbar,
        clear: clearDelayShowStationaryToolbar,
    } = useTimeout(() => {
        setShowStationaryToolbar(true);
    }, stationaryToolbarTransitionDuration);

    useEffect(() => {
        clearDelayShowStationaryToolbar();

        const stationaryToolbarVisibilityAction = resolveStationaryToolbarVisibilityAction({
            dockAnnotationEditorToViewport,
            showStationaryToolbar,
            stationaryToolbarIsActive,
            stationaryToolbarIsClosing,
        });

        if (!stationaryToolbarIsActive) {
            if (stationaryToolbarIsClosing) {
                setStationaryToolbarIsClosing(false);
            }
            if (stationaryToolbarVisibilityAction === 'hide') {
                setShowStationaryToolbar(false);
            }
            return;
        }

        if (stationaryToolbarVisibilityAction === 'show') {
            setShowStationaryToolbar(true);
            return;
        }

        if (stationaryToolbarVisibilityAction === 'delay-show') {
            delayShowStationaryToolbar();
        }
    }, [
        dockAnnotationEditorToViewport,
        showStationaryToolbar,
        stationaryToolbarIsClosing,
        stationaryToolbarIsActive,
    ]);

    useEffect(() => {
        if (!readOnly && !cachedValue) {
            setCachedValue(value);
        } else if (readOnly && cachedValue) {
            replaceSlateEditorValue({
                editor,
                value: JSON.stringify(cachedValue),
                selection: SLATE_EDITOR_REPLACEMENT_SELECTION.preserve,
            });
            setValue(cachedValue);
            setCachedValue(null);
        }
    }, [readOnly]);

    useEffect(() => {
        if (!showAnnotationEditorOverride || !postQuote) {
            appliedShowAnnotationEditorOverrideRef.current = false;
            return;
        }

        pendingReaderAnnotationEditorOpenSyncRef.current = false;
        if (readerAnnotationEditorOpenSyncTimeoutRef.current) {
            clearTimeout(readerAnnotationEditorOpenSyncTimeoutRef.current);
            readerAnnotationEditorOpenSyncTimeoutRef.current = null;
        }

        if (
            readOnly
            || isCommentEditor
            || showAnnotationEditor
            || appliedShowAnnotationEditorOverrideRef.current
        ) {
            return;
        }

        /**
         * Crossing the medium breakpoint swaps between the floating desktop
         * editor tree and the docked mobile editor tree. Reopen the remounted
         * editor from the existing quote so the reader sees the same editor in
         * the new layout instead of losing it at the breakpoint boundary.
         */
        appliedShowAnnotationEditorOverrideRef.current = true;
        setShowAnnotationEditor(true);
        if (handleShowAnnotationEditor) handleShowAnnotationEditor(true);
    }, [
        handleShowAnnotationEditor,
        isCommentEditor,
        postQuote,
        readOnly,
        showAnnotationEditor,
        showAnnotationEditorOverride,
    ]);

    useEffect(() => {
        if (
            readOnly
            || isCommentEditor
            || !showAnnotationEditor
            || preserveExistingAnnotationEditShell
        ) {
            return;
        }

        if (pendingReaderAnnotationEditorOpenSyncRef.current) {
            return;
        }

        if (showAnnotationEditorOverride && postQuote) {
            return;
        }

        /**
         * ReaderView owns the live quote and visibility flags. If that parent
         * state has already cleared, do not leave the local editor visible due
         * to a transient reopen race inside native outside-click handling.
         */
        setShowAnnotationEditor(false);
    }, [
        preserveExistingAnnotationEditShell,
        isCommentEditor,
        postQuote,
        readOnly,
        showAnnotationEditor,
        showAnnotationEditorOverride,
    ]);

    useEffect(() => {
        if (
            !readOnly
            && !isCommentEditor
            && !!postQuote
            && !showAnnotationEditor
            && postId
        ) {
            let shouldReopenFromFailure = false;
            try {
                shouldReopenFromFailure = sessionStorage.getItem(`reader-annotation-reopen:${postId}`) === '1';
            } catch (error) {
                shouldReopenFromFailure = false;
            }
            if (!shouldReopenFromFailure) return;

            setShowAnnotationEditor(true);
            if (handleShowAnnotationEditor) handleShowAnnotationEditor(true);
            if (updatePosition) updatePosition(false);
            if (shouldFocus) ReactEditor.focus(editor);
            try {
                sessionStorage.removeItem(`reader-annotation-reopen:${postId}`);
            } catch (error) {
                // Best-effort local recovery only
            }
        }
    }, [
        readOnly,
        isCommentEditor,
        postQuote,
        showAnnotationEditor,
        postId,
        shouldFocus,
        updatePosition,
        handleShowAnnotationEditor,
    ]);

    useEffect(() => () => {
        if (desktopSelectionCollapseRecheckTimeoutRef.current) {
            clearTimeout(desktopSelectionCollapseRecheckTimeoutRef.current);
            desktopSelectionCollapseRecheckTimeoutRef.current = null;
        }
        if (readerAnnotationEditorDeferredOpenSyncTimeoutRef.current) {
            clearTimeout(readerAnnotationEditorDeferredOpenSyncTimeoutRef.current);
            readerAnnotationEditorDeferredOpenSyncTimeoutRef.current = null;
        }
        if (readerAnnotationEditorOpenSyncTimeoutRef.current) {
            clearTimeout(readerAnnotationEditorOpenSyncTimeoutRef.current);
            readerAnnotationEditorOpenSyncTimeoutRef.current = null;
        }
        clearDeferredReaderSelectionFocus();
        clearDeferredReaderSelectionPositionUpdate();
    }, [
        clearDeferredReaderSelectionFocus,
        clearDeferredReaderSelectionPositionUpdate,
    ]);

    const stationaryToolbarCloseTimeoutIdRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    /**
     * Cancels any pending stationary-toolbar shell collapse before a reopen can
     * be undone by a stale delayed close callback.
     */
    const clearScheduledStationaryToolbarClose = useCallback((): void => {
        if (stationaryToolbarCloseTimeoutIdRef.current) {
            clearTimeout(stationaryToolbarCloseTimeoutIdRef.current);
            stationaryToolbarCloseTimeoutIdRef.current = null;
        }
    }, []);

    /**
     * Keeps the shell mounted until the stationary toolbar finishes its own
     * staggered exit transitions, then clears the active shell state.
     */
    const scheduleStationaryToolbarClose = useCallback((): void => {
        clearScheduledStationaryToolbarClose();
        stationaryToolbarCloseTimeoutIdRef.current = setTimeout(() => {
            stationaryToolbarCloseTimeoutIdRef.current = null;
            setStationaryToolbarIsClosing(false);
            setStationaryToolbarIsActive(false);
        }, stationaryToolbarCloseDelay);
    }, [
        clearScheduledStationaryToolbarClose,
        stationaryToolbarCloseDelay,
    ]);

    useEffect(() => () => {
        clearScheduledStationaryToolbarClose();
    }, [
        clearScheduledStationaryToolbarClose,
    ]);

    /**
     * Delay editor select all text so it takes
     * place after default selection behavior
     */
    const {
        start: timeoutSelectAllText,
    } = useTimeout(() => {
        // Select entire contents of the editor
        Transforms.select(editor, {
            anchor: Editor.start(editor, []),
            focus: Editor.end(editor, []),
        });
    }, SLATE_EDITOR_SELECT_ALL_TEXT_TIMEOUT_DURATION);

    /**
     * The docked annotation editor can stay mounted while hidden, so collapse
     * any stale toolkit state as soon as the editor itself is no longer shown.
     * This preserves the "reopen collapsed" behavior without fighting the
     * toolkit toggle while the editor is visible.
     */
    useEffect(() => {
        if (showAnnotationEditor) {
            return;
        }

        clearDelayShowStationaryToolbar();
        clearScheduledStationaryToolbarClose();

        if (stationaryToolbarIsClosing) {
            setStationaryToolbarIsClosing(false);
        }

        if (showStationaryToolbar) {
            setShowStationaryToolbar(false);
        }

        if (stationaryToolbarIsActive) {
            setStationaryToolbarIsActive(false);
        }
    }, [
        clearDelayShowStationaryToolbar,
        clearScheduledStationaryToolbarClose,
        showAnnotationEditor,
        showStationaryToolbar,
        stationaryToolbarIsActive,
        stationaryToolbarIsClosing,
    ]);

    useEffect(() => {
        if (
            readOnly
            || isCommentEditor
            || !isMobileKeyboardOffsetTarget
        ) {
            setMobileKeyboardOffset(0);
            return;
        }

        /**
         * Docked mobile editors already sit inside the ReaderView viewport
         * root, which tracks the visible viewport floor. Only floating editor
         * layouts need a separate keyboard lift inside AnnotationEditor.
         */
        const computeKeyboardOffset = (): void => {
            if (dockAnnotationEditorToViewport) {
                setMobileKeyboardOffset(0);
                return;
            }

            if (!window.visualViewport) {
                setMobileKeyboardOffset(0);
                return;
            }

            const layoutViewportHeight = Math.max(
                window.innerHeight || 0,
                document.documentElement.clientHeight || 0,
            );
            const { activeElement } = document;
            const editorHasFocus = !!containerRef.current
                && !!activeElement
                && containerRef.current.contains(activeElement);
            const visualViewportHeight = window.visualViewport.height;
            const nextOffset = resolveMobileAnnotationEditorKeyboardOffset({
                layoutViewportHeight,
                visualViewportHeight,
                editorHasFocus,
            });
            setMobileKeyboardOffset((prev) => (Math.abs(prev - nextOffset) < 1 ? prev : nextOffset));
        };

        let focusOutTimeoutId: ReturnType<typeof setTimeout> | undefined;
        const onFocusIn = (): void => computeKeyboardOffset();
        const onFocusOut = (): void => {
            if (focusOutTimeoutId) clearTimeout(focusOutTimeoutId);
            focusOutTimeoutId = setTimeout(computeKeyboardOffset, 75);
        };

        computeKeyboardOffset();
        window.visualViewport?.addEventListener('resize', computeKeyboardOffset);
        window.addEventListener('focusin', onFocusIn);
        window.addEventListener('focusout', onFocusOut);

        return () => {
            if (focusOutTimeoutId) clearTimeout(focusOutTimeoutId);
            window.visualViewport?.removeEventListener('resize', computeKeyboardOffset);
            window.removeEventListener('focusin', onFocusIn);
            window.removeEventListener('focusout', onFocusOut);
        };
    }, [
        dockAnnotationEditorToViewport,
        readOnly,
        isCommentEditor,
        isMobileKeyboardOffsetTarget,
    ]);

    useEffect(() => {
        if (uploadingStateChangeEvent) {
            handleUploadingMediaStateChange(
                uploadingStateChangeEvent.mediaItem,
                uploadingStateChangeEvent.snapshot,
                uploadingStateChangeEvent.progress,
            );
        }
    }, [uploadingStateChangeEvent]);

    useEffect(() => {
        if (uploadingCompleteEvent) {
            // Handle successful uploads on complete
            getDownloadURL(uploadingCompleteEvent.storageRef).then((downloadURL: string) => {
                handleUploadingMediaComplete(uploadingCompleteEvent.mediaItem, downloadURL);
            });
        }
    }, [uploadingCompleteEvent]);

    // Update Stationary Toolbar Height Value
    const {
        start: delayComputeStationaryToolbarHeight,
        clear: clearDelayComputeStationaryToolbarHeight,
    } = useTimeout(() => {
        if (containerRef.current) {
            const height = containerRef.current.clientHeight;
            setStationaryToolbarHeight(height);
        }
    }, stationaryToolbarLifecycleDelay);
    useEffect(() => {
        if (containerRef.current) {
            // Recompute height of stationary toolbar
            clearDelayComputeStationaryToolbarHeight();
            delayComputeStationaryToolbarHeight();
        }
    }, [
        value,
        stationaryToolbarIsActive,
        containerRef.current,
    ]);

    // Move Selection to Start of document if edit or cancel edit annotation
    useEffect(() => {
        if (!isEditing) {
            Transforms.select(editor, { path: [0, 0], offset: 0 });
        }
    }, [isEditing]);

    // Inspect Mouseup events to confirm if user has
    // used mouse to select text.
    // If so, show Annotation Editor.
    useEventListener(
        'mousedown',
        (e: Event) => {
            if (!postId || readOnly || isCommentEditor) return;
            if (annotationEditorDismissalBlocked(e)) {
                /**
                 * Modal and toolbar interactions can unmount their portal UI on
                 * mousedown, which leaves the follow-up mouseup/click looking
                 * like a plain outside tap. Suppress that short trailing burst.
                 */
                requestAnnotationEditorDismissalSuppression();
                mouseGestureRef.current = null;
                dismissAnnotationEditorOnMouseUpRef.current = false;
                dismissedAnnotationQuoteRef.current = null;
                return;
            }
            const mouseEvent = e as MouseEvent;
            if (mouseEvent.button !== 0) {
                mouseGestureRef.current = null;
                dismissAnnotationEditorOnMouseUpRef.current = false;
                dismissedAnnotationQuoteRef.current = null;
                return;
            }
            const outsideAnnotationEditor = eventTargetsOutsideAnnotationEditor(e);
            const targetEditor = e.composedPath().find((ele: EventTarget) => {
                const target = ele as HTMLElement;
                if (target.dataset) {
                    return target.dataset.id === postId;
                }

                return false;
            });
            if (
                showAnnotationEditor
                && outsideAnnotationEditor
                && !targetEditor
            ) {
                closeAnnotationEditor(ANNOTATION_EDITOR_CLOSE_REASON.mousedownOutsideAnnotationEditor);
                dismissAnnotationEditorOnMouseUpRef.current = true;
                dismissedAnnotationQuoteRef.current = postQuote
                    ? {
                        ...postQuote,
                        selections: [...postQuote.selections],
                        texts: [...postQuote.texts],
                    }
                    : null;
            } else {
                dismissAnnotationEditorOnMouseUpRef.current = false;
                dismissedAnnotationQuoteRef.current = null;
            }
            mouseGestureRef.current = {
                x: mouseEvent.clientX,
                y: mouseEvent.clientY,
                moved: false,
            };
        },
    );

    useEventListener(
        'mousemove',
        (e: Event) => {
            if (!mouseGestureRef.current) return;
            const mouseEvent = e as MouseEvent;
            if (
                Math.abs(mouseEvent.clientX - mouseGestureRef.current.x) > TOUCH_TAP_MAX_MOVEMENT_PX
                || Math.abs(mouseEvent.clientY - mouseGestureRef.current.y) > TOUCH_TAP_MAX_MOVEMENT_PX
            ) {
                mouseGestureRef.current = {
                    ...mouseGestureRef.current,
                    moved: true,
                };
            }
        },
    );

    useEventListener(
        'mouseup',
        (e: Event) => {
            if (!postId || readOnly || isCommentEditor) return;
            if (dismissAnnotationEditorOnMouseUpRef.current) {
                dismissAnnotationEditorOnMouseUpRef.current = false;
                const dismissedAnnotationQuote = dismissedAnnotationQuoteRef.current;
                dismissedAnnotationQuoteRef.current = null;

                /**
                 * Desktop outside-click dismissal happens on mousedown so the
                 * floating editor disappears immediately. Ignore the matching
                 * mouseup unless it created a genuinely new selection, because
                 * browsers often keep reporting the just-dismissed highlight
                 * for one more event tick.
                 */
                const nativeSelection = window.getSelection();
                const hasVisibleSelection = !!nativeSelection
                    && !nativeSelection.isCollapsed
                    && !!nativeSelection.toString()
                    && nativeSelection.toString().trim().length > 0;
                const selectionInsideAnnotationEditor = selectionTargetsAnnotationEditor(nativeSelection);
                if (!hasVisibleSelection || selectionInsideAnnotationEditor || !postEditor) {
                    let dismissalGuardReason = 'ignore-without-post-editor';
                    if (!hasVisibleSelection) {
                        dismissalGuardReason = 'ignore-without-visible-selection';
                    } else if (selectionInsideAnnotationEditor) {
                        dismissalGuardReason = 'ignore-selection-inside-annotation-editor';
                    }

                    logAnnotationEditDebug('annotation-read-highlight-mouseup-guard', {
                        checkpoint: 'mouseup-dismiss-guard',
                        reason: dismissalGuardReason,
                        nativeSelection: resolveNativeSelectionDebugPayload(nativeSelection),
                        showAnnotationEditor,
                    });
                    mouseGestureRef.current = null;
                    return;
                }

                const resolvedHighlightSelection = resolveReadHighlightSelection({
                    cachedSelection: postEditor.selection as Selection | null,
                    editor: postEditor,
                    nativeSelection,
                });
                const {
                    cachedSelection,
                    derivedSelection,
                    derivedSelectionMatchMode,
                    resolvedSelection,
                    resolvedSelectionSource,
                } = resolvedHighlightSelection;

                logReadHighlightSelectionDebug({
                    checkpoint: 'mouseup-dismiss-guard',
                    event: 'annotation-read-highlight-mouseup-guard',
                    nativeSelection,
                    postQuote: dismissedAnnotationQuote,
                    reason: resolvedSelection
                        ? 'resolved-selection'
                        : 'unresolved-selection',
                    showAnnotationEditor,
                    cachedSelection,
                    derivedSelection,
                    derivedSelectionMatchMode,
                    resolvedSelection,
                    resolvedSelectionSource,
                    selectionInsideAnnotationEditor,
                });

                if (
                    !resolvedSelection
                    || annotationQuoteMatchesSelection({
                        quote: dismissedAnnotationQuote,
                        selection: resolvedSelection.selection,
                        text: resolvedSelection.text,
                    })
                ) {
                    logReadHighlightSelectionDebug({
                        checkpoint: 'mouseup-dismiss-guard',
                        event: 'annotation-read-highlight-mouseup-guard',
                        nativeSelection,
                        postQuote: dismissedAnnotationQuote,
                        reason: !resolvedSelection
                            ? 'ignore-unresolved-selection'
                            : 'ignore-dismissed-quote-match',
                        showAnnotationEditor,
                        cachedSelection,
                        derivedSelection,
                        derivedSelectionMatchMode,
                        resolvedSelection,
                        resolvedSelectionSource,
                        selectionInsideAnnotationEditor,
                    });
                    mouseGestureRef.current = null;
                    return;
                }

                logReadHighlightSelectionDebug({
                    checkpoint: 'mouseup-dismiss-guard',
                    event: 'annotation-read-highlight-mouseup-guard',
                    nativeSelection,
                    postQuote: dismissedAnnotationQuote,
                    reason: 'reopen-with-new-selection',
                    showAnnotationEditor,
                    cachedSelection,
                    derivedSelection,
                    derivedSelectionMatchMode,
                    resolvedSelection,
                    resolvedSelectionSource,
                    selectionInsideAnnotationEditor,
                });
                handleHighlightPostText(e, false);
                mouseGestureRef.current = null;
                return;
            }
            const highlightPostTextResult = handleHighlightPostText(e, false);
            if (
                showAnnotationEditor
                && !highlightPostTextResult.highlightHandled
            ) {
                scheduleDesktopSelectionCollapseRecheck(e);
            }
            mouseGestureRef.current = null;
        },
    );

    // Inspect Keyup events to confirm if user has
    // used keys to select text.
    // If so, show selection toolbar.
    useEventListener(
        'keyup',
        (e: Event) => {
            if (
                !postId
                || readOnly
                || isCommentEditor
                || preserveExistingAnnotationEditShell
            ) return;
            handleHighlightPostText(e, true);
        },
    );

    useEventListener(
        'touchend',
        (e: Event) => {
            if (!postId || readOnly || isCommentEditor) return;
            handleHighlightPostText(e, false);
            touchGestureRef.current = null;
        },
    );

    /**
     * Treat touch scrolling and drag-selection gestures separately from a tap so
     * docked mobile annotation editor dismissal only happens on intentional taps.
     */
    useEventListener(
        'touchstart',
        (e: Event) => {
            if (!postId || readOnly || isCommentEditor) return;
            if (annotationEditorDismissalBlocked(e)) {
                /**
                 * Touch-owned modal/button interactions can finish after the
                 * portal has already closed, so keep the annotation draft
                 * mounted through the matching touchend/click sequence.
                 */
                requestAnnotationEditorDismissalSuppression();
                touchGestureRef.current = null;
                return;
            }
            const touchEvent = resolveTouchEvent(e);
            if (!touchEvent) return;
            const touch = touchEvent.touches[0];
            if (!touch) {
                touchGestureRef.current = null;
                return;
            }
            touchGestureRef.current = {
                x: touch.clientX,
                y: touch.clientY,
                moved: false,
            };
        },
    );

    useEventListener(
        'touchmove',
        (e: Event) => {
            if (!touchGestureRef.current) return;
            const touchEvent = resolveTouchEvent(e);
            if (!touchEvent) return;
            const touch = touchEvent.touches[0];
            if (!touch) return;
            if (
                Math.abs(touch.clientX - touchGestureRef.current.x) > TOUCH_TAP_MAX_MOVEMENT_PX
                || Math.abs(touch.clientY - touchGestureRef.current.y) > TOUCH_TAP_MAX_MOVEMENT_PX
            ) {
                touchGestureRef.current = {
                    ...touchGestureRef.current,
                    moved: true,
                };
            }
        },
    );

    useEventListener(
        'touchcancel',
        () => {
            touchGestureRef.current = null;
        },
    );

    // Detect copy
    useEventListener(
        'keydown',
        (e: Event) => {
            const keyboardEvent = e as KeyboardEvent;
            if (
                showAnnotationEditor
                && !isCommentEditor
                && !readOnly
                && !!postQuote
                && keyboardEvent.metaKey
                && keyboardEvent.key === 'c'
            ) {
                copyHighlightText();
            }
        },
    );

    const {
        start: timeoutResetEditorValue,
        clear: clearTimeoutResetEditorValue,
    } = useTimeout(() => {
        replaceSlateEditorValue({
            editor,
            value: JSON.stringify(INITIAL_EDITOR_VALUE),
            selection: SLATE_EDITOR_REPLACEMENT_SELECTION.start,
        });
        setValue(INITIAL_EDITOR_VALUE);
    }, EDITOR_TRANSITION_DURATION);

    // ===== Methods =====

    const updateSelectionToolbarPosition = (): void => {
        const nativeSelection = window.getSelection();
        let y = 24;
        let x = -52;
        if (
            nativeSelection
            && nativeSelection.rangeCount > 0
            && containerRef.current
            && selectionToolbarRef.current
        ) {
            const range = nativeSelection.getRangeAt(0);
            const rect = range.getBoundingClientRect();
            const toolbarWidth = selectionToolbarRef.current.children[0]
                ? selectionToolbarRef.current.children[0].clientWidth
                : 0;
            y = (rect.top - containerRef.current.getBoundingClientRect().top
                - (selectionToolbarRef.current.offsetHeight + 7)
            );
            x = Math.max(
                ((rect.left - containerRef.current.getBoundingClientRect().left + rect.width / 2)
                    - (toolbarWidth / 2)
                ),
                0,
            );
        }
        setSelectionToolbarPosition({
            x,
            y,
        });
    };

    // Function is reactive to changes to the slate value (making links)
    // and changes to the selection (placing cursor on links)
    const getSelectedLinkURL = useMemo(() => {
        if (editor.selection) {
            const links = WebLinkHelpers.getSelectionWebLinks(editor);
            if (links.length === 1) {
                const [link] = links;
                return (link[0] as WebLinkNode).href;
            }
        }
        return null;
    }, [
        value,
        editor.selection,
    ]);

    // Function is reactive to changes to the slate value (making links)
    // and changes to the selection (placing cursor on links)
    const getSelectedLinkID = useMemo(() => {
        if (editor.selection) {
            const links = WebLinkHelpers.getSelectionWebLinks(editor);
            if (links.length === 1) {
                const [link] = links;
                return (link[0]as WebLinkNode).id;
            }
        }
        return null;
    }, [
        value,
        editor.selection,
    ]);

    const moveCursorToEndOfDocument = useCallback((): void => {
        try {
            Transforms.select(editor, Editor.end(editor, []));
        } catch (err) {
            return;
        }
        BottomBufferHelpers.adjustSelectionAroundBottomBuffer(editor);
        ReactEditor.focus(editor);
    }, [editor]);

    const {
        start: delayMoveCursorToEndOfDocument,
        clear: clearDelayMoveCursorToEndOfDocument,
    } = useTimeout(() => {
        moveCursorToEndOfDocument();
    }, EDITOR_TRANSITION_DURATION);

    useLayoutEffect(() => {
        if (
            !showAnnotationEditor
            || !shouldFocus
            || !postQuote
            || pendingReaderSelectionFocusQuoteIdRef.current !== postQuote.id
        ) {
            if (!showAnnotationEditor || !postQuote) {
                clearDeferredReaderSelectionFocus();
            }
            return undefined;
        }

        let remainingAttempts = 4;

        const releaseDeferredSelectionFocus = (): void => {
            deferredReaderSelectionFocusRafRef.current = null;

            const renderedPostQuoteHighlights = Array.from(document.querySelectorAll<HTMLElement>('[data-quote=\'post-quote\']'))
                .filter((highlight) => highlight.isConnected);

            if (renderedPostQuoteHighlights.length === 0 && remainingAttempts > 0) {
                remainingAttempts -= 1;
                deferredReaderSelectionFocusRafRef.current = window.requestAnimationFrame(releaseDeferredSelectionFocus);
                return;
            }

            clearDeferredReaderSelectionFocus();
            moveCursorToEndOfDocument();
        };

        releaseDeferredSelectionFocus();

        return () => {
            if (deferredReaderSelectionFocusRafRef.current != null) {
                window.cancelAnimationFrame(deferredReaderSelectionFocusRafRef.current);
                deferredReaderSelectionFocusRafRef.current = null;
            }
        };
    }, [
        clearDeferredReaderSelectionFocus,
        moveCursorToEndOfDocument,
        postQuote,
        shouldFocus,
        showAnnotationEditor,
    ]);

    const updateCanSubmit = (val: Descendant[]): void => {
        const newValue = JSON.stringify(val);
        if (
            newValue !== SERIALIZED_TRUE_INITIAL_EDITOR_VALUE
            && newValue !== SERIALIZED_INITIAL_EDITOR_VALUE
        ) {
            setCanSubmit(true);
        } else if (
            canSubmit
            && (
                newValue === SERIALIZED_TRUE_INITIAL_EDITOR_VALUE
                || newValue === SERIALIZED_INITIAL_EDITOR_VALUE
            )
        ) {
            setCanSubmit(false);
        }
    };

    const getEditorPlainText = (): string => {
        try {
            return Editor.string(editor, []);
        } catch (error) {
            // Slate can transiently have no start text node during normalization on mobile.
            return value.map((node) => Node.string(node)).join('\n');
        }
    };

    const selectionIncludesHeaderElements = useCallback((selections: Selection[]): boolean => {
        if (!postEditor) {
            return false;
        }

        return selections.some((selection) => {
            try {
                return Array.from(Editor.nodes(postEditor, {
                    at: selection as Location,
                    match: HeaderElement.isHeaderElement,
                })).length > 0;
            } catch (error) {
                return false;
            }
        });
    }, [postEditor]);

    const handleSubmitButtonPress = (
        e: React.MouseEvent | React.TouchEvent | React.PointerEvent,
    ): void => {
        const now = Date.now();
        if (now - lastSendTriggerTimestampRef.current < 250) {
            return;
        }
        lastSendTriggerTimestampRef.current = now;
        e.preventDefault();
        handleSubmit();
    };

    const handleSubmit = (): void => {
        const hasDirtyAudioNotes = dirtyAudioNoteIds.size > 0;
        const hasSavingAudioNotes = savingAudioNoteIds.size > 0;
        const shouldBlockUnverifiedAnnotationWrite = (): boolean => {
            if (!user && setNotifyUserToSignUpAfterAnnotationCreationAttempt) {
                setNotifyUserToSignUpAfterAnnotationCreationAttempt(true);
                return true;
            }
            // We don't allow anonymous users to annotate or comment.
            if (
                user
                && user.authenticationType === AUTHENTICATION_TYPE.anonymous
                && setNotifyUserToSignUpAfterAnnotationCreationAttempt
            ) {
                setNotifyUserToSignUpAfterAnnotationCreationAttempt(true);
                return true;
            }
            // We don't allow users with unverified emails to annotate or comment.
            if (
                user
                && user.authenticationType === AUTHENTICATION_TYPE.email
                && !userEmailVerified
                && setIsUserProfileDialogExpanded
            ) {
                const annotationAction = isCommentEditor ? 'a comment' : 'an annotation';
                setSnackbarData(createAnnotationEditorSnackbar({
                    visible: true,
                    duration: DEFAULT_SNACKBAR_VISIBLE_DURATION,
                    text: `Verify your email to make ${annotationAction}. Click "${USER_VERIFY_EMAIL_BUTTON_TEXT}" in the User Dialog`,
                    icon: CautionIcon,
                    hasFailure: true,
                }));
                // Show user profile dialog, which has a button to verify email.
                setIsUserProfileDialogExpanded(true);
                return true;
            }

            return false;
        };
        const executeSend = (): void => {
            if (postQuote && !isEditing && submitAnnotation) {
                if (selectionIncludesHeaderElements(postQuote.selections)) {
                    setSnackbarData(createAnnotationEditorSnackbar({
                        visible: true,
                        duration: DEFAULT_SNACKBAR_VISIBLE_DURATION,
                        text: SNACKBAR_MESSAGE_HIGHLIGHT_HEADER_TEXT_ERROR,
                        icon: CautionIcon,
                        hasFailure: true,
                    }));
                    return;
                }

                // TODO if slow, do equality check by looking at intial elements of Slate Editor
                // i.e. One Line and Bottom Buffer
                const newValue = JSON.stringify(value, null, 2);
                const newText = getEditorPlainText();

                // Get annotation media
                // TODO: Add sketch too
                const figureNodes = Node.nodes(editor);
                const linkNodes = Node.nodes(editor);
                const audioNoteNodes = Node.nodes(editor);
                const annotationFigures = Array.from(figureNodes)
                    .filter(([node]) => FigureElement.isFigureElement(node))
                    .map(([node]) => (node as FigureNode).id);
                const annotationLinks = Array.from(linkNodes)
                    .filter(([node]) => WebLinkElement.isWebLinkElement(node))
                    .map(([node]) => (node as WebLinkNode).id);
                const annotationAudioNotes = Array.from(audioNoteNodes)
                    .filter(([node]) => AudioNoteElement.isAudioNoteElement(node))
                    .map(([node]) => (node as AudioNoteNode).id);
                const annotationMedia = [
                    ...annotationFigures,
                    ...annotationLinks,
                    ...annotationAudioNotes,
                ];

                // Submit Annotation
                submitAnnotation(newValue, newText, postQuote, annotationMedia);

                // Move Selection to Start of document
                Transforms.select(editor, { path: [0, 0], offset: 0 });

                // Close Emoji Selector if Open
                if (showEmojiSelector) {
                    setShowEmojiSelector(false);
                }

                if (shouldFocus) {
                    ReactEditor.focus(editor);
                }

                // Hide annotation Editor
                setShowAnnotationEditor(false);
                if (handleShowAnnotationEditor) handleShowAnnotationEditor(false);
                if (postQuote && setPostQuote) {
                    setPostQuote(null);
                }

                // Clear Annotation Editor
                clearTimeoutResetEditorValue();
                timeoutResetEditorValue();
            } else if (isCommentEditor && !isEditing && submitAnnotationComment) {
                // TODO if slow, do equality check by looking at intial elements of Slate Editor
                // i.e. One Line and Bottom Buffer
                const newValue = JSON.stringify(value, null, 2);
                const newText = getEditorPlainText();

                // Get annotation media
                // TODO: Add sketch too
                const figureNodes = Node.nodes(editor);
                const linkNodes = Node.nodes(editor);
                const audioNoteNodes = Node.nodes(editor);
                const annotationFigures = Array.from(figureNodes)
                    .filter(([node]) => FigureElement.isFigureElement(node))
                    .map(([node]) => (node as FigureNode).id);
                const annotationLinks = Array.from(linkNodes)
                    .filter(([node]) => WebLinkElement.isWebLinkElement(node))
                    .map(([node]) => (node as WebLinkNode).id);
                const annotationAudioNotes = Array.from(audioNoteNodes)
                    .filter(([node]) => AudioNoteElement.isAudioNoteElement(node))
                    .map(([node]) => (node as AudioNoteNode).id);
                const annotationMedia = [
                    ...annotationFigures,
                    ...annotationLinks,
                    ...annotationAudioNotes,
                ];
                const hasCommentText = newText.trim().length > 0;
                if (!hasCommentText && annotationMedia.length === 0) {
                    return;
                }

                // Submit Annotation
                const submitAccepted = submitAnnotationComment(newValue, newText, annotationMedia);
                if (!submitAccepted) {
                    return;
                }

                // Move Selection to Start of document
                Transforms.select(editor, { path: [0, 0], offset: 0 });

                // Close Emoji Selector if Open
                if (showEmojiSelector) {
                    setShowEmojiSelector(false);
                }

                if (shouldFocus) {
                    ReactEditor.focus(editor);
                }

                // Clear Annotation Editor
                const emptyValue = INITIAL_EDITOR_VALUE;
                replaceSlateEditorValue({
                    editor,
                    value: JSON.stringify(emptyValue),
                    selection: SLATE_EDITOR_REPLACEMENT_SELECTION.start,
                });
                setValue(INITIAL_EDITOR_VALUE);
            } else if (isEditing && editAnnotation) {
                // TODO if slow, do equality check by looking at intial elements of Slate Editor
                // i.e. One Line and Bottom Buffer
                const newValue = JSON.stringify(value, null, 2);
                const newText = getEditorPlainText();

                // Get annotation media
                // TODO: Add sketch too
                const figureNodes = Node.nodes(editor);
                const linkNodes = Node.nodes(editor);
                const audioNoteNodes = Node.nodes(editor);
                const annotationFigures = Array.from(figureNodes)
                    .filter(([node]) => FigureElement.isFigureElement(node))
                    .map(([node]) => (node as FigureNode).id);
                const annotationLinks = Array.from(linkNodes)
                    .filter(([node]) => WebLinkElement.isWebLinkElement(node))
                    .map(([node]) => (node as WebLinkNode).id);
                const annotationAudioNotes = Array.from(audioNoteNodes)
                    .filter(([node]) => AudioNoteElement.isAudioNoteElement(node))
                    .map(([node]) => (node as AudioNoteNode).id);
                const annotationMedia = [
                    ...annotationFigures,
                    ...annotationLinks,
                    ...annotationAudioNotes,
                ];

                // clear cached value so that we don't revert to it
                setCachedValue(null);

                // Edit Annotation
                editAnnotation(newValue, newText, annotationMedia);

                // Close Emoji Selector if Open
                if (showEmojiSelector) {
                    setShowEmojiSelector(false);
                }
            } else {
                throw new Error('There was a problem creating annotation.');
            }
        };

        if (!readOnly && shouldBlockUnverifiedAnnotationWrite()) {
            return;
        }

        if (isCommentEditor && !readOnly) {
            if (hasDirtyAudioNotes || hasSavingAudioNotes) {
                return;
            }
            executeSend();
            return;
        }

        if (isRecordingAudioNote) {
            // Inform user that they are attempting to save while audio
            // recording in progress
            setModal({
                visible: true,
                message: SAVE_POST_DURING_AUDIO_RECORDING_ERROR,
                proceedCallback: () => {
                    if (!readOnly) executeSend();
                    setModal({
                        visible: false,
                        message: modal.message,
                    });
                },
                rejectCallback: () => {
                    setModal({
                        visible: false,
                        message: modal.message,
                    });
                },
            });
        } else if (!readOnly) {
            if (hasDirtyAudioNotes || hasSavingAudioNotes) {
                return;
            }
            executeSend();
        }
    };

    const handleEmojiButton = (e: React.MouseEvent): void => {
        e.preventDefault();
        setShowEmojiSelector(!showEmojiSelector);
    };

    const handleStationaryToolbarButton = (e: React.MouseEvent): void => {
        e.preventDefault();

        const stationaryToolbarToggleAction = resolveStationaryToolbarToggleAction({
            stationaryToolbarIsActive,
            stationaryToolbarIsClosing,
            showStationaryToolbar: resolvedShowStationaryToolbar,
        });

        /**
         * Docked mobile toolkits need a real closing phase so the tool groups
         * can finish their exit animation before the shell collapses.
         */
        if (stationaryToolbarToggleAction === 'close') {
            clearDelayShowStationaryToolbar();
            setStationaryToolbarIsClosing(true);
            setShowStationaryToolbar(false);
            scheduleStationaryToolbarClose();
            return;
        }

        clearDelayShowStationaryToolbar();
        clearScheduledStationaryToolbarClose();
        setStationaryToolbarIsClosing(false);
        setStationaryToolbarIsActive(true);
        if (dockAnnotationEditorToViewport) {
            setShowStationaryToolbar(true);
        }
    };

    const handleRecordAudio = (e: React.MouseEvent): void => {
        e.preventDefault();

        // Select end of document if no selection
        if (!editor.selection) {
            moveCursorToEndOfDocument();
        }

        // Initiate Auto Record
        setAutoRecord(true);

        // Create Audio Note
        AudioNoteHelpers.insertAudioNote(editor, {
            id: uuidv4(),
            filePath: AUDIO_NOTE_AUTO_RECORD_IDENTIFIER,
        });

        const figureNodes = Node.nodes(editor);
        const linkNodes = Node.nodes(editor);
        const annotationFigures = Array.from(figureNodes)
            .filter(([node]) => FigureElement.isFigureElement(node))
            .map(([node]) => (node as FigureNode).id);
        const annotationLinks = Array.from(linkNodes)
            .filter(([node]) => WebLinkElement.isWebLinkElement(node))
            .map(([node]) => (node as WebLinkNode).id);
        const noText = Array.from(Node.nodes(editor))
            .reduce((empty, [node]) => empty && Node.string(node).length === 0, true);
        if (
            annotationFigures.length === 0
            && annotationLinks.length === 0
            && noText
        ) {
            // Move cursor to end of document only if audio note is first thing
            // user places in annotation
            moveCursorToEndOfDocument();
        }
    };

    const addUploadingMedia = (mediaItem: IMediaItem): void => {
        setUploadingMedia(new Map([
            ...Array.from(uploadingMedia.entries()),
            [mediaItem.id, mediaItem],
        ]));
    };

    const updateUploadingMedia = (mediaItem: IMediaItem): void => {
        setUploadingMedia(new Map([
            ...Array.from(uploadingMedia.entries()),
            [mediaItem.id, mediaItem],
        ]));
    };

    const removeUploadingMedia = (mediaId: string): void => {
        if (uploadingMedia.has(mediaId)) {
            uploadingMedia.delete(mediaId);
            setUploadingMedia(new Map([
                ...Array.from(uploadingMedia.entries()),
            ]));
        }
    };

    const onEditableMouseEnter = (e: React.MouseEvent): void => {
        if (!readOnly) {
            onCursorEnter(
                CURSOR_TARGET.annotator,
                [CURSOR_SIGN.click],
                e.currentTarget as HTMLElement,
            );
        }
    };

    const onEditableMouseLeave = (e: React.MouseEvent): void => {
        if (!readOnly) onCursorLeave(e);
    };

    const onEditableFocus = (): void => {
        setInputFocused(true);

        // Play Sound
        if (hasSound) {
            const audioClip = getOrCreateAudioClip({
                audioRef: inputClickClip,
                src: InputClick,
                volume: DEFAULT_AUDIO_VOLUME,
            });
            audioClip.pause();
            audioClip.currentTime = 0;
            playAudio(audioClip);
        }
    };

    const onEditableBlur = (): void => {
        setInputFocused(false);
    };

    /**
     * Rich clipboard payloads can be reduced by the browser before Slate's
     * beforeinput handler sees them, so paste directly from the original event
     * whenever that clipboardData is available.
     */
    const handleEditablePaste = useCallback((event: React.ClipboardEvent<HTMLDivElement>): boolean => {
        if (readOnly) {
            return false;
        }

        return handleEditorClipboardPaste({
            editor,
            event,
        });
    }, [
        editor,
        readOnly,
    ]);

    const onButtonMouseEnter = (e: React.MouseEvent | React.TouchEvent): void => {
        onCursorEnter(
            CURSOR_TARGET.editorButton,
            [CURSOR_SIGN.click],
            e.currentTarget as HTMLElement,
        );
    };

    const onButtonMouseLeave = (e: React.MouseEvent | React.TouchEvent): void => {
        onCursorLeave(e);
    };

    /**
     * Audio notes report dirty/saving membership by id so submit gating can
     * stay fully parent-driven and deterministic.
     */
    const setAudioNoteDirtyState = useCallback((audioNoteId: string, isDirty: boolean): void => {
        setDirtyAudioNoteIds((ids) => {
            const alreadyDirty = ids.has(audioNoteId);
            if (alreadyDirty === isDirty) {
                return ids;
            }

            const updated = new Set(ids);
            if (isDirty) {
                updated.add(audioNoteId);
            } else {
                updated.delete(audioNoteId);
            }
            return updated;
        });
    }, []);

    /**
     * Saving membership is tracked separately so the tooltip can distinguish
     * between a local draft and an in-flight save.
     */
    const setAudioNoteSavingState = useCallback((audioNoteId: string, isSaving: boolean): void => {
        setSavingAudioNoteIds((ids) => {
            const alreadySaving = ids.has(audioNoteId);
            if (alreadySaving === isSaving) {
                return ids;
            }

            const updated = new Set(ids);
            if (isSaving) {
                updated.add(audioNoteId);
            } else {
                updated.delete(audioNoteId);
            }
            return updated;
        });
    }, []);

    const handleUploadingMediaComplete = (
        mediaItem: IMediaItem,
        fileURL: string,
    ): void => {
        // Select end of document if no selection
        if (!editor.selection) {
            moveCursorToEndOfDocument();
        }

        // Update uploading media item
        updateUploadingMedia({
            // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
            ...uploadingMedia.get(mediaItem.id)!,
            url: fileURL,
        });

        const fileType = mediaItem.file.type.split('/')[0];
        switch (fileType) {
        case 'audio': {
            // Create Audio Note
            AudioNoteHelpers.insertAudioNote(editor, {
                id: mediaItem.id,
                filePath: mediaItem.filePath,
                title: mediaItem.file.name,
                uploaded: true,
            });
            break;
        }
        case 'image': {
            // Create Figure
            FigureHelpers.insertFigure(
                editor,
                mediaItem.id,
                undefined,
                mediaItem.filePath,
                mediaItem.file.name,
            );
            break;
        }
        default:
            break;
        }
    };

    const handleUploadingMediaStateChange = (
        mediaItem: IMediaItem,
        _snapshot: UploadTaskSnapshot,
        progress: number,
    ): void => {
        if (uploadingMedia.has(mediaItem.id)) {
            updateUploadingMedia({
                // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                ...uploadingMedia.get(mediaItem.id)!,
                uploadProgress: progress,
            });
        } else {
            addUploadingMedia(mediaItem);
        }
    };

    const checkForEscape = (e: React.KeyboardEvent<HTMLInputElement>): void => {
        if (e.key === KEYCODE.escape) {
            const annotationEditor = document.querySelector(`[data-id='${id}']`);
            if (annotationEditor && !isCommentEditor) {
                // Blur Input
                (annotationEditor as HTMLElement).blur();
                // Hide Editor
                setShowAnnotationEditor(false);
                if (handleShowAnnotationEditor) handleShowAnnotationEditor(false);
                if (postQuote && setPostQuote) {
                    setPostQuote(null);
                }
            }
        }
    };

    const onImageLoad = (): void => {
        setRerenderMeasuringElements(true);
        if (remeasureParentHeight) remeasureParentHeight();
    };

    const onEmbedLoad = (): void => {
        setRerenderMeasuringElements(true);
        if (remeasureParentHeight) remeasureParentHeight();
    };

    const onAudioNoteAdjustHeight = (): void => {
        setRerenderMeasuringElements(true);
        if (remeasureParentHeight) remeasureParentHeight();
    };

    const copyHighlightText = (): void => {
        if (!postQuote || readOnly) return;
        if (postQuote.texts.length > 1) throw new Error('Encountered unexpected non-contiguous selection in Annotatio Editor.');

        const fallbackHighlightText = postQuote.texts[0]; // Will always be a contiguous selection
        const serializedHighlight = (
            postEditor
            && postQuote.selections[0]
            && serializeSlateSelectionForClipboard({
                editor: postEditor,
                selection: postQuote.selections[0],
            })
        ) || null;
        const highlightText = serializedHighlight?.text || fallbackHighlightText;
        const highlightHtml = serializedHighlight?.html;
        copyToClipboard(
            highlightText,
            () => {
                setSnackbarData(createAnnotationEditorSnackbar({
                    visible: true,
                    duration: DEFAULT_SNACKBAR_VISIBLE_DURATION,
                    text: SNACKBAR_MESSAGE_COPY_HIGHLIGHT_TEXT_SUCCESS,
                    icon: ClipboardIcon,
                }));
            },
            () => {
                setSnackbarData(createAnnotationEditorSnackbar({
                    visible: true,
                    duration: DEFAULT_SNACKBAR_VISIBLE_DURATION,
                    text: SNACKBAR_MESSAGE_COPY_HIGHLIGHT_TEXT_ERROR,
                    icon: CautionIcon,
                    hasFailure: true,
                }));
            },
            highlightHtml,
        );

        if (currentSessionId) {
            // Record user action
            recordUserAction({
                test: isTestMode(),
                type: USER_ACTION_TYPE.copyTextHighlight,
                sessionId: currentSessionId,
                payload: {
                    postId,
                    text: highlightText,
                },
            });
        }
    };

    /**
     * Run copy from the button click itself. On touch browsers this maps to
     * the trusted touch-end activation sequence, which aligns better with
     * WebKit's clipboard gating than the earlier pointer-down path.
     */
    const handleCopyHighlightTextClick = (e: React.MouseEvent): void => {
        e.preventDefault();
        e.stopPropagation();

        /**
         * Mobile Safari can focus the copy button before the clipboard helper
         * runs. Re-focus the Slate editor first so any temporary legacy-copy
         * fallback restores focus to the live editor instead of leaving the
         * keyboard dismissed behind the button.
         */
        if (touchViewportHasCoarsePointer && !readOnly) {
            ReactEditor.focus(editor);
        }

        copyHighlightText();
    };

    // ===== Rendering =====

    const renderModal = (): React.ReactElement => (
        <Modal
            hasContainer
            annotationEditorOwned
            onCloseInteractionStart={requestAnnotationEditorDismissalSuppression}
            customCursorActive={customCursorActive}
            hasSound={hasSound}
            isOpen={modal.visible}
            closeModal={modal.rejectCallback}
        >
            <GenericModal
                customCursorActive={customCursorActive}
                message={modal.message}
                callback={modal.proceedCallback}
                hideCancel={!modal.rejectCallback}
                onCursorEnter={onCursorEnter}
                onCursorLeave={onCursorLeave}
            />
        </Modal>
    );

    const renderElement = useCallback(({
        element,
        attributes,
        children,
    }: {
        element: any,
        attributes: any,
        children: any,
    }) => {
        if (BlockQuoteElement.isBlockQuoteElement(element)) {
            return (
                <BlockQuote
                    attributes={attributes}
                >
                    {children}
                </BlockQuote>
            );
        }

        if (BottomBufferElement.isBottomBufferElement(element)) {
            return (
                <BottomBuffer
                    adjustSelectionAroundBottomBuffer={() => BottomBufferHelpers.adjustSelectionAroundBottomBuffer(editor)}
                    height={0}
                    attributes={attributes}
                >
                    {children}
                </BottomBuffer>
            );
        }

        if (CaptionElement.isCaptionElement(element)) {
            return (
                <Caption
                    id={element.id}
                    text={Node.string(element)}
                    contentType={element.figureContentType}
                    attributes={attributes}
                    placeholder="Write a caption..."
                    onCursorEnter={onCursorEnter}
                    onCursorLeave={onCursorLeave}
                >
                    {children}
                </Caption>
            );
        }

        if (DividerElement.isDividerElement(element)) {
            return (
                <Divider
                    color={color}
                    attributes={attributes}
                >
                    {children}
                </Divider>
            );
        }

        if (FigureElement.isFigureElement(element)) {
            if (FigureElement.isFigureElement(element)) {
                const { url, filePath, id: figureId } = element.children[0];
                switch (element.figureContentType) {
                case FigureContentTypes.youtube:
                    return (
                        <YouTubeEmbed
                            id={figureId}
                            url={url}
                            color={color}
                            readOnly={readOnly}
                            user={user}
                            currentSessionId={currentSessionId}
                            postId={postId}
                            editorType={type}
                            attributes={attributes}
                            onLoad={onEmbedLoad}
                        >
                            {children}
                        </YouTubeEmbed>
                    );
                case FigureContentTypes.twitter:
                    return (
                        <TwitterEmbed
                            id={figureId}
                            url={url}
                            color={color}
                            readOnly={readOnly}
                            user={user}
                            currentSessionId={currentSessionId}
                            postId={postId}
                            editorType={type}
                            attributes={attributes}
                            onCursorEnter={onCursorEnter}
                            onCursorLeave={onCursorLeave}
                            onLoad={onEmbedLoad}
                        >
                            {children}
                        </TwitterEmbed>
                    );
                case FigureContentTypes.spotify:
                    return (
                        <SpotifyEmbed
                            url={url}
                            color={color}
                            attributes={attributes}
                            onLoad={onEmbedLoad}
                        >
                            {children}
                        </SpotifyEmbed>
                    );
                case FigureContentTypes.vimeo:
                    return (
                        <VimeoEmbed
                            id={figureId}
                            url={url}
                            color={color}
                            readOnly={readOnly}
                            user={user}
                            currentSessionId={currentSessionId}
                            postId={postId}
                            editorType={type}
                            attributes={attributes}
                            onLoad={onEmbedLoad}
                        >
                            {children}
                        </VimeoEmbed>
                    );
                default:
                    // Note: We leave out parentRef because Figure in Messaging
                    // Editor can't be fullscreen
                    return (
                        <Figure
                            customCursorActive={customCursorActive}
                            currentSessionId={currentSessionId}
                            url={url}
                            user={user}
                            postId={postId}
                            readOnly={readOnly}
                            filePath={filePath}
                            editorType={type}
                            attributes={attributes}
                            layoutType={element.orientation}
                            isGalleryChild={element.isGalleryChild}
                            rerender={rerenderMeasuringElements}
                            setRerender={setRerenderMeasuringElements}
                            createGallery={(imgId, imgURL, path) => FigureHelpers.bundleFigureWithNewFigureIntoGallery(editor, imgId, imgURL, path)}
                            uploadingMedia={uploadingMedia}
                            setUploadingMedia={addUploadingMedia}
                            updateUploadingMedia={updateUploadingMedia}
                            onCursorEnter={onCursorEnter}
                            onCursorLeave={onCursorLeave}
                            setSnackbarData={setSnackbarData}
                        >
                            {children}
                        </Figure>
                    );
                }
            }
        }

        if (AudioNoteElement.isAudioNoteElement(element)) {
            // containerRef.current.childNodes[0] makes parentRef the Slate Context
            return (
                <AudioNote
                    id={element.id}
                    customCursorActive={customCursorActive}
                    user={user}
                    userIsAdmin={userIsAdmin}
                    small={!stationaryToolbarIsActive}
                    hasSound={hasSound}
                    editorType={type}
                    buttonWidth={EDITOR_BUTTON_LENGTH}
                    filePath={element.filePath}
                    title={element.title}
                    description={element.description}
                    color={color}
                    attributes={attributes}
                    readOnly={readOnly}
                    currentSessionId={currentSessionId}
                    postId={postId}
                    parentRef={containerRef.current?.children[0] as HTMLElement}
                    autoRecord={autoRecord}
                    handleAutoRecord={setAutoRecord}
                    autoRecordIdentifier={AUDIO_NOTE_AUTO_RECORD_IDENTIFIER}
                    isRecordingAudioNote={isRecordingAudioNote}
                    setIsRecordingAudioNote={setIsRecordingAudioNote}
                    revealOptionsAbove={false}
                    setAudioNoteDirtyState={setAudioNoteDirtyState}
                    setAudioNoteSavingState={setAudioNoteSavingState}
                    broadcastHeightAdjusted={onAudioNoteAdjustHeight}
                    uploadingMedia={uploadingMedia}
                    setUploadingMedia={addUploadingMedia}
                    updateUploadingMedia={updateUploadingMedia}
                    removeUploadingMedia={removeUploadingMedia}
                    updateAudioNote={({
                        id: audioNoteId,
                        filePath,
                        title,
                        uploaded,
                        description,
                    }) => AudioNoteHelpers.updateAudioNote(editor, {
                        id: audioNoteId,
                        filePath,
                        title,
                        uploaded,
                        description,
                    })}
                    {...(readOnly
                        ? {
                            isAuthor,
                        } : {}
                    )}
                    onCursorEnter={onCursorEnter}
                    onCursorLeave={onCursorLeave}
                    requestEditorDismissalSuppression={requestAnnotationEditorDismissalSuppression}
                    setInputFocused={setInputFocused}
                    setSnackbarData={setSnackbarData}
                >
                    {children}
                </AudioNote>
            );
        }

        if (HeaderElement.isH1(element)) {
            return (
                <HeaderOne
                    isFirstElement={element.isFirstElement}
                    readOnly={readOnly}
                    plainText={Node.string(element)}
                    attributes={attributes}
                >
                    {children}
                </HeaderOne>
            );
        }

        if (HeaderElement.isH2(element)) {
            return (
                <HeaderTwo
                    isFirstElement={element.isFirstElement}
                    paragraphId={element.paragraphId}
                    plainText={Node.string(element)}
                    attributes={attributes}
                >
                    {children}
                </HeaderTwo>
            );
        }

        if (FigureContentElement.isFigureContentElement(element)) {
            if (element.figureContentType === FigureContentTypes.image) {
                return (
                    <Image
                        id={element.id}
                        customCursorActive={customCursorActive}
                        currentSessionId={currentSessionId}
                        color={color}
                        user={user}
                        postId={postId}
                        readOnly={readOnly}
                        editorType={type}
                        imagePath={element.filePath}
                        attributes={attributes}
                        layoutType={element.orientation}
                        isGalleryChild={element.isGalleryChild}
                        onLoad={onImageLoad}
                        setImagePath={(filePath) => FigureHelpers.setFigureUrlFilePath(editor, undefined, filePath)}
                        setLayout={(orientation) => FigureHelpers.setFigureOrientation(editor, orientation)}
                        removeFigure={() => FigureHelpers.removeFigure(editor)}
                        swapFigureOrder={() => FigureHelpers.shuffleGalleryOrder(editor)}
                        uploadingMedia={uploadingMedia}
                        setUploadingMedia={addUploadingMedia}
                        updateUploadingMedia={updateUploadingMedia}
                        onCursorEnter={onCursorEnter}
                        onCursorLeave={onCursorLeave}
                        setSnackbarData={setSnackbarData}
                    >
                        {children}
                    </Image>
                );
            }

            return (
                <HiddenVoidContent
                    {...attributes}
                >
                    {children}
                </HiddenVoidContent>
            );
        }

        if (WebLinkElement.isWebLinkElement(element)) {
            return (
                <InlineLink
                    id={element.id}
                    customCursorActive={customCursorActive}
                    color={color}
                    href={element.href}
                    readOnly={readOnly}
                    currentSessionId={currentSessionId}
                    postId={postId}
                    editorType={type}
                    onCursorEnter={onCursorEnter}
                    onCursorLeave={onCursorLeave}
                    parentRef={containerRef.current}
                    setSnackbarData={setSnackbarData}
                    attributes={attributes}
                >
                    {children}
                </InlineLink>
            );
        }

        if (ListElement.isListElement(element)) {
            const listType = ListElement.isOL(element)
                ? ListElement.LIST_TYPE.ordered : ListElement.LIST_TYPE.unordered;
            return (
                <List
                    type={listType}
                    attributes={attributes}
                >
                    {children}
                </List>
            );
        }

        if (ListItemElement.isListItemElement(element)) {
            return (
                <ListItem
                    attributes={attributes}
                >
                    {children}
                </ListItem>
            );
        }

        return (
            <Paragraph
                attributes={attributes}
                fontFamily={FONT_TYPE.PLUS_JAKARTA_SANS}
            >
                {children}
            </Paragraph>
        );
    }, [
        postId,
        color,
        user,
        userIsAdmin,
        hasSound,
        autoRecord,
        type,
        readOnly,
        currentSessionId,
        uploadingMedia,
        setUploadingMedia,
        updateUploadingMedia,
        containerRef.current,
        isRecordingAudioNote,
        rerenderMeasuringElements,
        setAudioNoteDirtyState,
        setAudioNoteSavingState,
        stationaryToolbarIsActive,
        onAudioNoteAdjustHeight,
        onCursorEnter,
        onCursorLeave,
        setInputFocused,
        setSnackbarData,
        onImageLoad,
        onEmbedLoad,
        customCursorActive,
    ]);

    const renderLeaf = useCallback(({
        attributes,
        children,
        leaf,
    }: {
        attributes: any,
        children: any,
        leaf: any,
    }) => (
        <SlateLeaf
            {...attributes}
            {...(leaf.dictationHighlight && {
                'data-dictation': 'annotation-dictation',
            })}
            bold={leaf[EMPHASIZER_MARK.bold]}
            italics={leaf[EMPHASIZER_MARK.italicize]}
            underline={leaf[EMPHASIZER_MARK.underline]}
            strikethrough={leaf[EMPHASIZER_MARK.strikethrough]}
            highlightColor={leaf.dictationHighlight
                ? leaf.highlightColor
                : null}
        >
            {children}
        </SlateLeaf>
    ), []);

    const renderPlaceholder = useCallback(({
        attributes,
        children,
    }: {
        attributes: any,
        children: any,
    }) => (
        <SlatePlaceholder
            fontMultiplier={SLATE_PLACEHOLDER_FONT_MULTIPLIER}
            {...attributes}
        >
            {children}
        </SlatePlaceholder>
    ), []);

    /**
     * The controlled Slate value is the stable source of truth here.
     *
     * During the read-only -> edit transition, Slate's mutable editor instance
     * can lag behind `value` for a render. Using `editor.children` here lets a
     * populated annotation temporarily look empty, which collapses the shell
     * into a blank white block.
     */
    const annotationIsEmpty = useMemo(() => {
        const currentValueEditor = {
            children: value,
        } as Editor;
        const figureNodes = Node.nodes(currentValueEditor);
        const linkNodes = Node.nodes(currentValueEditor);
        const audioNoteNodes = Node.nodes(currentValueEditor);
        const annotationFigures = Array.from(figureNodes)
            .filter(([node]) => FigureElement.isFigureElement(node))
            .map(([node]) => (node as FigureNode).id);
        const annotationLinks = Array.from(linkNodes)
            .filter(([node]) => WebLinkElement.isWebLinkElement(node))
            .map(([node]) => (node as WebLinkNode).id);
        const annotationAudioNotes = Array.from(audioNoteNodes)
            .filter(([node]) => AudioNoteElement.isAudioNoteElement(node))
            .map(([node]) => (node as AudioNoteNode).id);
        const noText = Array.from(Node.nodes(currentValueEditor))
            .reduce((empty, [node]) => empty && Node.string(node).length === 0, true);
        return noText
            && annotationFigures.length === 0
            && annotationLinks.length === 0
            && annotationAudioNotes.length === 0;
    }, [value]);

    useEffect(() => {
        let containerBackgroundColor: string | null = null;
        let containerColor: string | null = null;
        let containerDisplay: string | null = null;
        let containerVisibility: string | null = null;
        let containerOpacity: string | null = null;

        if (typeof window !== 'undefined' && containerRef.current) {
            const computedStyle = window.getComputedStyle(containerRef.current);
            containerBackgroundColor = computedStyle.backgroundColor;
            containerColor = computedStyle.color;
            containerDisplay = computedStyle.display;
            containerVisibility = computedStyle.visibility;
            containerOpacity = computedStyle.opacity;
        }

        logAnnotationEditDebug('annotation-editor-shell-state', {
            annotationId: id,
            readOnly,
            isEditing,
            shouldFocus,
            showAnnotationEditor,
            showAnnotationEditorOverride,
            hasPostQuote: !!postQuote,
            pendingReaderAnnotationEditorOpenSync: pendingReaderAnnotationEditorOpenSyncRef.current,
            readerAnnotationEditorParentOpenState: {
                ...readerAnnotationEditorParentOpenStateRef.current,
            },
            annotationIsEmpty,
            hasMessage: !!message,
            messageNodeCount: getSlateValueNodeCount(slateValue),
            messageTextLength: getSlateValueTextLength(slateValue),
            valueNodeCount: getSlateValueNodeCount(value),
            valueTextLength: getSlateValueTextLength(value),
            editorNodeCount: getSlateValueNodeCount(editor.children as Descendant[]),
            editorTextLength: getSlateValueTextLength(editor.children as Descendant[]),
            containerTextLength: containerRef.current?.textContent?.trim().length || 0,
            containerBackgroundColor,
            containerColor,
            containerDisplay,
            containerVisibility,
            containerOpacity,
            width,
            dockAnnotationEditorToViewport,
            pinAnnotationEditorShellToViewport,
        });
    }, [
        annotationIsEmpty,
        dockAnnotationEditorToViewport,
        editor,
        postQuote,
        id,
        isEditing,
        message,
        pinAnnotationEditorShellToViewport,
        readOnly,
        shouldFocus,
        showAnnotationEditor,
        showAnnotationEditorOverride,
        slateValue,
        value,
        width,
    ]);

    const canSubmitAction = useMemo(() => {
        if (canSubmit) return true;
        if (!isCommentEditor) return false;
        const commentText = getEditorPlainText().trim();
        return commentText.length > 0;
    }, [
        canSubmit,
        isCommentEditor,
        value,
    ]);

    const numberDirtyAudioNotes = dirtyAudioNoteIds.size;
    const numberSavingAudioNotes = savingAudioNoteIds.size;
    const submitButtonDisabledReason = useMemo(() => resolveAnnotationSubmitButtonDisabledReason({
        interactionDisabled,
        numberSavingAudioNotes,
        numberDirtyAudioNotes,
        uploadingMediaCount: uploadingMedia.size,
    }), [
        interactionDisabled,
        numberSavingAudioNotes,
        numberDirtyAudioNotes,
        uploadingMedia.size,
    ]);
    const submitButtonIsEnabled = canSubmitAction && !submitButtonDisabledReason;

    const submitButtonEventHandlers = useMemo(() => {
        if (submitButtonDisabledReason) return {};
        if (isCommentEditor) {
            return {
                onClick: handleSubmitButtonPress,
            };
        }

        return {
            onTouchStart: handleSubmitButtonPress,
            onMouseDown: handleSubmitButtonPress,
            onClick: handleSubmitButtonPress,
        };
    }, [
        submitButtonDisabledReason,
        isCommentEditor,
        handleSubmitButtonPress,
    ]);

    return (
        <Container
            ref={containerRef}
            {...(!readOnly
                && viewportDimensions
                && typeof xPos === 'number'
                && typeof yPos === 'number'
                ? {
                    bottom: viewportDimensions.height - yPos - DEFAULT_ANNOTATION_EDITOR_HEIGHT,
                    left: xPos,
                } : {}
            )}
            width={width}
            readOnly={readOnly}
            boxShadow={!!boxShadow}
            backgroundColor={backgroundColor}
            isComment={isComment}
            isCommentEditor={!!isCommentEditor}
            disablePositionTransition={disablePositionTransition}
            mobileKeyboardOffset={mobileKeyboardOffset}
            hide={!showAnnotationEditor}
            transitionDuration={EDITOR_TRANSITION_DURATION}
            buttonContainerHeight={EDITOR_BUTTON_CONTAINER_HEIGHT}
            withPadding={!annotationIsEmpty && !stationaryToolbarIsActive && !readOnly}
            showAnnotationEditor={showAnnotationEditor}
            dockToViewport={dockAnnotationEditorToViewport}
            pinToViewportShell={pinAnnotationEditorShellToViewport}
        >
            {renderModal()}
            <SlateContainer
                {...(!readOnly
                    ? {
                        ...getRootProps(),
                    }
                    : {}
                )}
                {...(readOnly && !customCursorActive && (annotationIsFocused != null && annotationIsFocused)
                    ? {
                        style: {
                            cursor: 'text',
                        },
                    } : {}
                )}
                readOnly={readOnly}
                isDragAccept={isDragAccept}
                isDragReject={isDragReject}
                isDragActive={isDragActive}
                buttonCount={NUM_EDITOR_BUTTONS}
                buttonLength={EDITOR_BUTTON_LENGTH}
                canSubmit={canSubmit}
                annotationIsEmpty={annotationIsEmpty}
                buttonContainerHeight={EDITOR_BUTTON_CONTAINER_HEIGHT}
                buttonContainerPadding={EDITOR_BUTTON_CONTAINER_PADDING}
                stationaryToolbarIsActive={stationaryToolbarIsActive}
                dragContainerMargin={SLATE_CONTAINER_DRAG_ACTIVE_MARGIN_RIGHT}
                fontMultiplier={fontMultiplier}
                backgroundColor={backgroundColor}
                isCommentEditor={!!isCommentEditor}
            >
                <Slate
                    editor={editor}
                    value={value}
                    onChange={(val) => setValue(val)}
                >
                    <Editable
                        {...(readOnly
                            ? {
                                readOnly: true,
                            } : {}
                        )}
                        id={id}
                        data-id={id}
                        renderElement={renderElement}
                        renderLeaf={renderLeaf}
                        renderPlaceholder={renderPlaceholder}
                        placeholder={placeholder}
                        className={`${HOVER_TARGET_CLASSNAME} ${SLATE_EDITOR_CLASSNAME}`}
                        {...(!readOnly
                            ? { spellCheck: true }
                            : {}
                        )}
                        decorate={decorate}
                        onKeyDown={handleOnKeyDown}
                        onKeyUp={checkForEscape}
                        {...(!readOnly ? {
                            onMouseEnter: onEditableMouseEnter,
                            onMouseLeave: onEditableMouseLeave,
                        } : {
                            onMouseEnter: onHoverEnter,
                            // we leave out onMouseLeave because it prematurely eliminates cursor
                            // when still hovering over annotation
                        })}
                        onFocus={onEditableFocus}
                        onBlur={onEditableBlur}
                        onPaste={handleEditablePaste}
                    />
                    <PortableToolbar
                        // PostEditor Only
                        editorType={type}
                        editorId={id}
                        // Common to PostEditor and AnnotationEditor
                        customCursorActive={customCursorActive}
                        currentSessionId={currentSessionId}
                        type={EDITOR_TOOLBAR_TYPE.selection}
                        user={user}
                        postId={postId}
                        hasSound={hasSound}
                        readOnly={readOnly}
                        buttonLength={TOOLBAR_BUTTON_LENGTH}
                        setRef={(ref) => { selectionToolbarRef.current = ref; }}
                        color={color}
                        value={value}
                        selectedLinkURL={getSelectedLinkURL}
                        selectedLinkID={getSelectedLinkID}
                        top={selectionToolbarPosition.y}
                        left={selectionToolbarPosition.x}
                        defaultOpenGroups={new Map([
                            [EDITOR_TOOLBAR_TOOL_GROUP.layout, true],
                            [EDITOR_TOOLBAR_TOOL_GROUP.media, true],
                            [EDITOR_TOOLBAR_TOOL_GROUP.text, true],
                            [EDITOR_TOOLBAR_TOOL_GROUP.embeddings, true],
                        ])}
                        selection={editor.selection}
                        setSelection={(selection) => Transforms.select(editor, selection)}
                        updatePosition={updateSelectionToolbarPosition}
                        toggleBold={() => EmphasizerHelpers.toggleBold(editor)}
                        containsBold={EmphasizerHelpers.selectionContainsBold(editor)}
                        toggleItalics={() => EmphasizerHelpers.toggleItalicize(editor)}
                        containsItalics={EmphasizerHelpers.selectionContainsItalicize(editor)}
                        toggleStrikethrough={() => EmphasizerHelpers.toggleStrikethrough(editor)}
                        containsStrikethrough={EmphasizerHelpers.selectionContainsStrikethrough(editor)}
                        toggleUnderline={() => EmphasizerHelpers.toggleUnderline(editor)}
                        containsUnderline={EmphasizerHelpers.selectionContainsUnderline(editor)}
                        toggleHeaderOne={() => HeaderHelpers.toggleH1(editor)}
                        containsHeaderOne={HeaderHelpers.hasSelectedH1(editor)}
                        toggleHeaderTwo={() => HeaderHelpers.toggleH2(editor)}
                        containsHeaderTwo={HeaderHelpers.hasSelectedH2(editor)}
                        toggleNumberedList={() => ListHelpers.toggleOL(editor)}
                        containsNumberedList={ListHelpers.hasSelectedOL(editor)}
                        toggleBulletedList={() => ListHelpers.toggleUL(editor)}
                        containsBulletedList={ListHelpers.hasSelectedUL(editor)}
                        toggleQuote={() => BlockQuoteHelpers.toggleBlockQuote(editor)}
                        containsQuote={BlockQuoteHelpers.hasSelectedBlockQuote(editor)}
                        toggleDivider={() => DividerHelpers.toggleDivider(editor)}
                        containsDivider={DividerHelpers.hasSelectedDivider(editor)}
                        createFigure={(figureId, filePath, caption) => FigureHelpers.insertFigure(editor, figureId, undefined, filePath, caption)}
                        containsFigure={FigureHelpers.hasSelectedImageFigure(editor)}
                        createLink={(linkId, href, selection) => {
                            if (selection) {
                                Transforms.select(editor, selection);
                            }
                            WebLinkHelpers.wrapSelectionInLinkCmd(editor, linkId, href);
                        }}
                        updateLink={(linkId, href) => WebLinkHelpers.setWebLinkHref(editor, linkId, href)}
                        removeLink={() => WebLinkHelpers.removeWebLink(editor)}
                        containsLink={WebLinkHelpers.hasSelectedLink(editor)}
                        selectEndDocument={moveCursorToEndOfDocument}
                        createYouTubeEmbedding={(embeddingId, src) => FigureHelpers.insertFigure(editor, embeddingId, src)}
                        containsYouTube={FigureHelpers.hasSelectedYouTubeFigure(editor)}
                        createTwitterEmbedding={(embeddingId, src) => FigureHelpers.insertFigure(editor, embeddingId, src)}
                        containsTwitter={FigureHelpers.hasSelectedTwitterFigure(editor)}
                        createSpotifyEmbedding={(embeddingId, src) => FigureHelpers.insertFigure(editor, embeddingId, src)}
                        containsSpotify={FigureHelpers.hasSelectedSpotifyFigure(editor)}
                        createVimeoEmbedding={(embeddingId, src) => FigureHelpers.insertFigure(editor, embeddingId, src)}
                        containsVimeo={FigureHelpers.hasSelectedVimeoFigure(editor)}
                        insertAudioNote={(audioNoteId) => AudioNoteHelpers.insertAudioNote(editor, { id: audioNoteId })}
                        containsAudioNote={AudioNoteHelpers.hasSelectedAudioNote(editor)}
                        uploadingMedia={uploadingMedia}
                        setUploadingMedia={addUploadingMedia}
                        updateUploadingMedia={updateUploadingMedia}
                        onCursorEnter={onCursorEnter}
                        onCursorLeave={onCursorLeave}
                        setInputFocused={setInputFocused}
                        setSnackbarData={setSnackbarData}
                        pinLinkInputAboveEditor
                        requestAnnotationEditorDismissalSuppression={requestAnnotationEditorDismissalSuppression}
                    />
                    <Transition
                        in={showEmojiSelector && !!width}
                        timeout={{
                            enter: EMOJI_SELECTOR_TRANSITION_DURATION,
                            exit: EMOJI_SELECTOR_TRANSITION_DURATION,
                        }}
                        mountOnEnter
                    >
                        {
                            (state) => (
                                <EmojiSelectorContainer
                                    height={EMOJI_SELECTOR_HEIGHT}
                                    width={width!}
                                    stationaryToolbarHeight={stationaryToolbarHeight}
                                    stationaryEditorMinHeight={STATIONARY_EDITOR_MIN_HEIGHT}
                                    isActive={showEmojiSelector}
                                    style={{
                                        ...FADE_IN_DEFAULT_STYLE({
                                            direction: 'up',
                                            offset: 20,
                                            duration: EMOJI_SELECTOR_TRANSITION_DURATION,
                                            easing: themeObj.motion.eagerEasing,
                                        }),
                                        ...FADE_IN_TRANSITION_STYLES({
                                            direction: 'up',
                                            offset: 20,
                                        })[state],
                                    }}
                                >
                                    <EmojiSelector
                                        compact
                                        customCursorActive={customCursorActive}
                                        hasSound={hasSound}
                                        color={color}
                                        width={width!}
                                        responsiveLayout={false}
                                        handleEmojiSelect={(emojiObj) => {
                                            ReactEditor.focus(editor);
                                            if (emojiObj) Transforms.insertText(editor, emojiObj.char);
                                        }}
                                        onCursorEnter={onCursorEnter}
                                        onCursorLeave={onCursorLeave}
                                        setInputFocused={setInputFocused}
                                    />
                                </EmojiSelectorContainer>
                            )
                        }
                    </Transition>
                </Slate>
            </SlateContainer>
            {!readOnly && (
                <>
                    <DropzoneInput {...getInputProps()} />
                    <StationaryEditorContainer
                        isActive={stationaryToolbarIsActive}
                        buttonLength={EDITOR_BUTTON_LENGTH}
                        buttonMargin={EDITOR_BUTTON_MARGIN}
                        canSubmit={canSubmit}
                        buttonContainerPadding={EDITOR_BUTTON_CONTAINER_PADDING}
                        buttonCount={NUM_EDITOR_BUTTONS}
                        transitionDuration={stationaryToolbarTransitionDuration}
                        stationaryEditorMinHeight={STATIONARY_EDITOR_MIN_HEIGHT}
                    >
                        <StationaryToolbar
                            // Annotation Editor Only
                            show={showAnnotationEditor && resolvedShowStationaryToolbar}
                            // Common to PostEditor and AnnotationEditor
                            customCursorActive={customCursorActive}
                            currentSessionId={currentSessionId}
                            user={user}
                            postId={postId}
                            hasSound={hasSound}
                            color={color}
                            buttonLength={TOOLBAR_BUTTON_LENGTH}
                            defaultOpenGroups={stationaryToolbarDefaultOpenGroups}
                            selection={editor.selection}
                            setSelection={(selection) => Transforms.select(editor, selection)}
                            toggleBold={() => EmphasizerHelpers.toggleBold(editor)}
                            containsBold={EmphasizerHelpers.selectionContainsBold(editor)}
                            toggleItalics={() => EmphasizerHelpers.toggleItalicize(editor)}
                            containsItalics={EmphasizerHelpers.selectionContainsItalicize(editor)}
                            toggleStrikethrough={() => EmphasizerHelpers.toggleStrikethrough(editor)}
                            containsStrikethrough={EmphasizerHelpers.selectionContainsStrikethrough(editor)}
                            toggleUnderline={() => EmphasizerHelpers.toggleUnderline(editor)}
                            containsUnderline={EmphasizerHelpers.selectionContainsUnderline(editor)}
                            toggleHeaderOne={() => HeaderHelpers.toggleH1(editor)}
                            containsHeaderOne={HeaderHelpers.hasSelectedH1(editor)}
                            toggleHeaderTwo={() => HeaderHelpers.toggleH2(editor)}
                            containsHeaderTwo={HeaderHelpers.hasSelectedH2(editor)}
                            toggleNumberedList={() => ListHelpers.toggleOL(editor)}
                            containsNumberedList={ListHelpers.hasSelectedOL(editor)}
                            toggleBulletedList={() => ListHelpers.toggleUL(editor)}
                            containsBulletedList={ListHelpers.hasSelectedUL(editor)}
                            toggleQuote={() => BlockQuoteHelpers.toggleBlockQuote(editor)}
                            containsQuote={BlockQuoteHelpers.hasSelectedBlockQuote(editor)}
                            toggleDivider={() => DividerHelpers.toggleDivider(editor)}
                            containsDivider={DividerHelpers.hasSelectedDivider(editor)}
                            createFigure={(figureId, filePath, caption) => FigureHelpers.insertFigure(editor, figureId, undefined, filePath, caption)}
                            containsFigure={FigureHelpers.hasSelectedImageFigure(editor)}
                            createLink={(linkId, href, selection) => {
                                if (selection) {
                                    Transforms.select(editor, selection);
                                }
                                WebLinkHelpers.wrapSelectionInLinkCmd(editor, linkId, href);
                                WebLinkHelpers.wrapSelectionInLinkCmd(editor, linkId, href);
                            }}
                            // 1.We leave out containsLink because Block Toolbar should not show it as applied
                            // 2. We leave out updateLink, removeLink because Stationary Toolbar cannot present
                            // link card with these actions.
                            selectEndDocument={moveCursorToEndOfDocument}
                            createYouTubeEmbedding={(embeddingId, src) => FigureHelpers.insertFigure(editor, embeddingId, src)}
                            containsYouTube={FigureHelpers.hasSelectedYouTubeFigure(editor)}
                            createTwitterEmbedding={(embeddingId, src) => FigureHelpers.insertFigure(editor, embeddingId, src)}
                            containsTwitter={FigureHelpers.hasSelectedTwitterFigure(editor)}
                            createSpotifyEmbedding={(embeddingId, src) => FigureHelpers.insertFigure(editor, embeddingId, src)}
                            containsSpotify={FigureHelpers.hasSelectedSpotifyFigure(editor)}
                            createVimeoEmbedding={(embeddingId, src) => FigureHelpers.insertFigure(editor, embeddingId, src)}
                            containsVimeo={FigureHelpers.hasSelectedVimeoFigure(editor)}
                            insertAudioNote={(audioNoteId) => AudioNoteHelpers.insertAudioNote(editor, { id: audioNoteId })}
                            containsAudioNote={AudioNoteHelpers.hasSelectedAudioNote(editor)}
                            uploadingMedia={uploadingMedia}
                            setUploadingMedia={addUploadingMedia}
                            updateUploadingMedia={updateUploadingMedia}
                            onCursorEnter={onCursorEnter}
                            onCursorLeave={onCursorLeave}
                            setInputFocused={setInputFocused}
                            setSnackbarData={setSnackbarData}
                            pinLinkInputAboveEditor
                            editorHeight={containerRef.current?.clientHeight || stationaryToolbarHeight}
                            requestAnnotationEditorDismissalSuppression={requestAnnotationEditorDismissalSuppression}
                        />
                    </StationaryEditorContainer>
                    <ButtonContainer
                        isEditing={!!isEditing}
                        readOnly={readOnly}
                        isCommentEditor={!!isCommentEditor}
                        showAnnotationEditor={showAnnotationEditor}
                        height={EDITOR_BUTTON_CONTAINER_HEIGHT}
                        padding={EDITOR_BUTTON_CONTAINER_PADDING}
                    >
                        <EditorButton
                            className={HOVER_TARGET_CLASSNAME}
                            customCursorActive={customCursorActive}
                            length={EDITOR_BUTTON_LENGTH}
                            padding={EDITOR_BUTTON_PADDING}
                            margin={EDITOR_BUTTON_MARGIN}
                            {...getTouchMouseEventHandlers({
                                onMouseEnter: onButtonMouseEnter,
                                onMouseLeave: onButtonMouseLeave,
                            })}
                            {...getPressEventHandlers({ onMouseDown: handleRecordAudio })}
                        >
                            <Tooltip
                                text="Record"
                                side={isCommentEditor
                                    ? TOOLTIP_TYPE.top
                                    : TOOLTIP_TYPE.bottom}
                            />
                            <EditorButtonIcon
                                color={color}
                                length={EDITOR_BUTTON_LENGTH - 2 * (EDITOR_BUTTON_PADDING)}
                            >
                                <ReactSVG
                                    src={MicrophoneIcon}
                                />
                            </EditorButtonIcon>
                        </EditorButton>
                        <EditorButton
                            className={HOVER_TARGET_CLASSNAME}
                            customCursorActive={customCursorActive}
                            color={color}
                            length={EDITOR_BUTTON_LENGTH}
                            padding={EDITOR_BUTTON_PADDING}
                            margin={EDITOR_BUTTON_MARGIN}
                            isActive={stationaryToolbarIsActive}
                            {...getTouchMouseEventHandlers({
                                onMouseEnter: onButtonMouseEnter,
                                onMouseLeave: onButtonMouseLeave,
                            })}
                            {...getPressEventHandlers({ onMouseDown: handleStationaryToolbarButton })}
                        >
                            <Tooltip
                                text="Toolkit"
                                side={isCommentEditor
                                    ? TOOLTIP_TYPE.top
                                    : TOOLTIP_TYPE.bottom}
                            />
                            <EditorButtonIcon
                                color={color}
                                length={EDITOR_BUTTON_LENGTH - 2 * (EDITOR_BUTTON_PADDING)}
                            >
                                <ReactSVG
                                    src={ToolsIcon}
                                />
                            </EditorButtonIcon>
                        </EditorButton>
                        <EditorButton
                            className={HOVER_TARGET_CLASSNAME}
                            customCursorActive={customCursorActive}
                            color={color}
                            length={EDITOR_BUTTON_LENGTH}
                            padding={EDITOR_BUTTON_PADDING}
                            margin={EDITOR_BUTTON_MARGIN}
                            isActive={showEmojiSelector}
                            {...getTouchMouseEventHandlers({
                                onMouseEnter: onButtonMouseEnter,
                                onMouseLeave: onButtonMouseLeave,
                            })}
                            {...getPressEventHandlers({ onMouseDown: handleEmojiButton })}
                        >
                            <Tooltip
                                text="Emojis"
                                side={isCommentEditor
                                    ? TOOLTIP_TYPE.top
                                    : TOOLTIP_TYPE.bottom}
                            />
                            <EditorButtonIcon
                                color={color}
                                length={EDITOR_BUTTON_LENGTH - 2 * (EDITOR_BUTTON_PADDING)}
                            >
                                <ReactSVG
                                    src={SmileyIcon}
                                />
                            </EditorButtonIcon>
                        </EditorButton>
                        <Transition
                            in={isEditing}
                            timeout={{
                                enter: FADE_IN_TRANSITION_DURATION,
                                exit: FADE_IN_TRANSITION_DURATION,
                            }}
                            appear
                            mountOnEnter
                            unmountOnExit
                        >
                            {(state) => (
                                <SendButton
                                    className={HOVER_TARGET_CLASSNAME}
                                    customCursorActive={customCursorActive}
                                    color={themeObj.verascopeColor.red300}
                                    length={EDITOR_BUTTON_LENGTH}
                                    canSubmit
                                    stationaryToolbarIsActive={stationaryToolbarIsActive}
                                    isEditing={isEditing}
                                    {...getTouchMouseEventHandlers({
                                        onMouseEnter: onButtonMouseEnter,
                                        onMouseLeave: onButtonMouseLeave,
                                    })}
                                    {...getPressEventHandlers({ onMouseDown: cancelEditAnnotation! })}
                                    style={{
                                        ...FADE_IN_DEFAULT_STYLE({
                                            direction: 'right',
                                            offset: 10,
                                            easing: themeObj.motion.overshoot,
                                        }),
                                        ...FADE_IN_TRANSITION_STYLES({
                                            direction: 'right',
                                            offset: 10,
                                        })[state],
                                    }}
                                >
                                    <ReactSVG
                                        src={CrossIcon}
                                    />
                                </SendButton>
                            )}
                        </Transition>
                        <Transition
                            in={canSubmitAction}
                            timeout={{
                                enter: FADE_IN_TRANSITION_DURATION,
                                exit: FADE_IN_TRANSITION_DURATION,
                            }}
                            appear
                            mountOnEnter
                            unmountOnExit
                        >
                            {(state) => (
                                <SendButton
                                    className={HOVER_TARGET_CLASSNAME}
                                    customCursorActive={customCursorActive}
                                    color={isEditing ? themeObj.verascopeColor.green300 : color}
                                    length={EDITOR_BUTTON_LENGTH}
                                    canSubmit={submitButtonIsEnabled}
                                    allowInteraction={!!submitButtonDisabledReason}
                                    stationaryToolbarIsActive={stationaryToolbarIsActive}
                                    isEditing={isEditing}
                                    marginLeft={isEditing}
                                    title={submitButtonDisabledReason || undefined}
                                    aria-disabled={!!submitButtonDisabledReason}
                                    {...(!submitButtonDisabledReason
                                        ? getTouchMouseEventHandlers({
                                            onMouseEnter: onButtonMouseEnter,
                                            onMouseLeave: onButtonMouseLeave,
                                        })
                                        : {}
                                    )}
                                    {...submitButtonEventHandlers}
                                    style={{
                                        ...FADE_IN_DEFAULT_STYLE({
                                            direction: 'right',
                                            offset: 10,
                                            easing: themeObj.motion.overshoot,
                                        }),
                                        ...FADE_IN_TRANSITION_STYLES({
                                            direction: 'right',
                                            offset: 10,
                                        })[state],
                                    }}
                                >
                                    {!!submitButtonDisabledReason && (
                                        <Tooltip
                                            text={submitButtonDisabledReason}
                                            side={isCommentEditor
                                                ? TOOLTIP_TYPE.top
                                                : TOOLTIP_TYPE.bottom}
                                        />
                                    )}
                                    <ReactSVG
                                        src={isEditing ? CheckmarkIcon : ArrowIcon}
                                    />
                                </SendButton>
                            )}
                        </Transition>
                    </ButtonContainer>
                    <Transition
                        in={isDragActive}
                        timeout={{
                            enter: FADE_IN_TRANSITION_DURATION,
                            exit: FADE_IN_TRANSITION_DURATION,
                        }}
                    >
                        {(transitionState) => (
                            <DropzoneCompactMessageContainer
                                isDragActive={isDragActive}
                                isDragReject={isDragReject}
                                stationaryToolbarIsActive={stationaryToolbarIsActive}
                                style={{
                                    ...FADE_IN_DEFAULT_STYLE({
                                        direction: stationaryToolbarIsActive ? 'left' : 'right',
                                        offset: 10,
                                    }),
                                    ...FADE_IN_TRANSITION_STYLES({
                                        direction: stationaryToolbarIsActive ? 'left' : 'right',
                                        offset: 10,
                                    })[transitionState],
                                }}
                            >
                                <DropzoneCompactMessageIcon
                                    length={EDITOR_BUTTON_LENGTH - 2 * (EDITOR_BUTTON_PADDING)}
                                >
                                    <ReactSVG
                                        src={isDragReject
                                            ? CrossIcon
                                            : UploadIcon}
                                    />
                                </DropzoneCompactMessageIcon>
                                <DropzoneCompactMessageText>
                                    {isDragReject
                                        ? 'Invalid'
                                        : 'Valid'}
                                </DropzoneCompactMessageText>
                            </DropzoneCompactMessageContainer>
                        )}
                    </Transition>
                    {width
                    && (
                        <UploadProgressContainer
                            showEmojiSelector={showEmojiSelector}
                            emojiSelectorWidth={width}
                            stationaryToolbarHeight={stationaryToolbarHeight}
                            stationaryToolbarIsActive={stationaryToolbarIsActive}
                        >
                            {Array.from(uploadingMedia.values()).map((item, index) => (
                                <Transition
                                    key={item.id}
                                    in={uploadingMedia.size > 0}
                                    timeout={{
                                        enter: Math.min(
                                            MAX_FADE_IN_STAGGER_TRANSITION_DURATION,
                                            (index * FADE_IN_STAGGER_OFFSET_DURATION)
                                            + FADE_IN_STAGGER_TRANSITION_DURATION
                                            + UPLOAD_TRANSITION_DELAY,
                                        ),
                                        exit: Math.min(
                                            MAX_FADE_IN_STAGGER_TRANSITION_DURATION,
                                            ((uploadingMedia.size - Math.max(index - 1, 0)) * FADE_IN_STAGGER_OFFSET_DURATION)
                                            + FADE_IN_STAGGER_TRANSITION_DURATION,
                                        ),
                                    }}
                                    appear
                                    mountOnEnter
                                >
                                    {(transitionState) => (
                                        <UploadingMediaItem
                                            compact
                                            currentSessionId={currentSessionId}
                                            progress={item.uploadProgress}
                                            mediaItem={item}
                                            onItemCompletion={removeUploadingMedia}
                                            color={color}
                                            style={{
                                                ...FADE_IN_STAGGER_DEFAULT_STYLE({
                                                    direction: 'up',
                                                    offset: 10,
                                                }),
                                                ...FADE_IN_STAGGER_TRANSITION_STYLES({
                                                    direction: 'up',
                                                    offset: 10,
                                                    numItems: uploadingMedia.size,
                                                    index,
                                                    enterDelay: UPLOAD_TRANSITION_DELAY,
                                                })[transitionState],
                                            }}
                                        />
                                    )}
                                </Transition>
                            ))}
                        </UploadProgressContainer>
                    )}
                </>
            )}
            <Transition
                in={!readOnly && showAnnotationEditor && !isCommentEditor && !isEditing}
                timeout={{
                    enter: EDITOR_TRANSITION_DURATION,
                    exit: 0,
                }}
                appear
                mountOnEnter
                unmountOnExit
            >
                {(state) => (
                    <CopyTextContainer
                        buttonLength={COPY_TEXT_BUTTON_CONTAINER_HEIGHT}
                        editorHeight={EDITOR_BUTTON_CONTAINER_HEIGHT}
                        dockAboveEditor={dockCopyTextButtonAboveEditor}
                        style={{
                            ...FADE_IN_DEFAULT_STYLE({
                                direction: dockCopyTextButtonAboveEditor ? 'up' : 'right',
                                offset: 10,
                                easing: themeObj.motion.overshoot,
                            }),
                            ...FADE_IN_TRANSITION_STYLES({
                                direction: dockCopyTextButtonAboveEditor ? 'up' : 'right',
                                offset: 10,
                            })[state],
                        }}
                    >
                        <Button
                            className={HOVER_TARGET_CLASSNAME}
                            customCursorActive={customCursorActive}
                            type={BUTTON_TYPE.solid}
                            background={themeObj.color.neutral800}
                            height={COPY_TEXT_BUTTON_CONTAINER_HEIGHT}
                            width={COPY_TEXT_BUTTON_CONTAINER_HEIGHT}
                            icon={ClipboardIcon}
                            tooltip={{
                                active: true,
                                text: 'Copy Text',
                                side: copyTextTooltipSide,
                            }}
                            {...getTouchMouseEventHandlers({
                                onMouseEnter: onButtonMouseEnter,
                                onMouseLeave: onButtonMouseLeave,
                            })}
                            onClick={handleCopyHighlightTextClick}
                        />
                    </CopyTextContainer>
                )}
            </Transition>
        </Container>
    );
}

// ===== Styled Components =====

interface ContainerProps {
    bottom?: number,
    left?: number,
    width?: number,
    hide: boolean,
    readOnly: boolean,
    boxShadow: boolean,
    withPadding: boolean,
    isComment?: boolean,
    isCommentEditor: boolean,
    mobileKeyboardOffset: number,
    backgroundColor?: string,
    transitionDuration: number,
    buttonContainerHeight: number,
    showAnnotationEditor: boolean,
    disablePositionTransition: boolean,
    dockToViewport: boolean,
    pinToViewportShell: boolean,
}
const Container = styled.div<ContainerProps>`
    position: ${({ bottom, left, dockToViewport }) => {
        /**
         * Docked mobile rendering uses a stable body portal, so ignore the
         * floating desktop coordinates and let the mobile override anchor it.
         */
        if (dockToViewport) {
            return 'relative';
        }

        return typeof bottom === 'number' && typeof left === 'number'
            ? 'absolute'
            : 'relative';
    }};
    bottom: ${({ bottom, dockToViewport }) => {
        if (dockToViewport) {
            return 'auto';
        }

        return typeof bottom === 'number' ? `${bottom}px` : 'auto';
    }};
    left: ${({ left, dockToViewport }) => {
        if (dockToViewport) {
            return 'auto';
        }

        return typeof left === 'number' ? `${left}px` : 'auto';
    }};
    display: flex;
    flex-direction: column;
    width: ${({ width, isComment, readOnly }) => {
        if (isComment && readOnly) {
            return 'auto';
        }
        if (width) {
            return `${width}px`;
        }

        return '100%';
    }};
    max-width: 100%;
    max-height: ${({ readOnly }) => (readOnly ? 'none' : `${18.75 * BODY_FONT_SIZE}px`)};
    padding-bottom: ${({ withPadding }) => `${withPadding ? 2.5 * BODY_FONT_SIZE : 0}px`};
    background: ${({ backgroundColor, theme }) => backgroundColor || theme.color.white};
    border-radius: ${({ readOnly, buttonContainerHeight }) => (!readOnly
        ? `${buttonContainerHeight / 2}px`
        : `${0.3125 * BODY_FONT_SIZE}px`
    )};
    box-shadow: ${({ boxShadow, theme }) => (boxShadow
        ? theme.color.boxShadow100
        : 'none'
    )};
    z-index: ${({ readOnly }) => (!readOnly ? ANNOTATION_EDITOR_Z_INDEX : 'auto')};
    ${({ hide }) => hide && `
        opacity: 0;
        pointer-events: none;
    `};
    transition: ${({
        transitionDuration,
        disablePositionTransition,
        dockToViewport,
        theme,
    }) => {
        const positionTransitionDuration = resolveAnnotationEditorPositionTransitionDuration({
            disablePositionTransition,
            dockToViewport,
            transitionDuration,
        });
        const positionTransitionEasing = resolveAnnotationEditorPositionTransitionEasing({
            disablePositionTransition,
            dockToViewport,
            theme,
        });

        return `
            bottom ${positionTransitionDuration}ms ${positionTransitionEasing},
            left ${positionTransitionDuration}ms ${positionTransitionEasing},
            transform ${positionTransitionDuration}ms ${positionTransitionEasing},
            opacity ${transitionDuration}ms ${theme.motion.overshoot}
        `;
    }};

    ${({
        readOnly,
        isCommentEditor,
        theme,
        pinToViewportShell,
        showAnnotationEditor,
    }) => {
        if (
            readOnly
            || isCommentEditor
            || !pinToViewportShell
            || showAnnotationEditor
        ) {
            return '';
        }

        /**
         * A dormant touch-mobile annotation editor stays mounted in the body
         * portal so the first highlight can open it without a remount. Keep the
         * shell collapsed at the viewport floor so it cannot push the reader
         * layout around before the editor actually becomes visible. The hidden
         * shell must also stay non-interactive so its parked button rail never
         * steals taps from the reader content underneath.
         */
        return `
            ${theme.mediaQuery.small} {
                position: absolute;
                bottom: 0;
                left: 0;
                right: 0;
                width: 100%;
                max-height: 0;
                padding-bottom: 0;
                overflow: hidden;
                pointer-events: none;
                border-bottom-left-radius: 0px;
                border-bottom-right-radius: 0px;
                z-index: ${CONTENTS_TABLE_Z_INDEX};
            }

            ${theme.mediaQuery.extraSmall} {
                position: absolute;
                bottom: 0;
                left: 0;
                right: 0;
                width: 100%;
                max-height: 0;
                padding-bottom: 0;
                overflow: hidden;
                pointer-events: none;
                border-bottom-left-radius: 0px;
                border-bottom-right-radius: 0px;
                z-index: ${CONTENTS_TABLE_Z_INDEX};
            }
        `;
    }}

    ${({
        readOnly,
        isCommentEditor,
        theme,
        mobileKeyboardOffset,
        showAnnotationEditor,
    }) => {
        if (readOnly || isCommentEditor || !showAnnotationEditor) {
            return '';
        }

        const dockedBottomCss = resolveAnnotationEditorBottomCss({
            mobileKeyboardOffset,
        });
        return `
            ${theme.mediaQuery.small} {
                position: absolute;
                ${dockedBottomCss}
                left: 0;
                right: 0;
                width: 100%;
                pointer-events: auto;
                border-bottom-left-radius: 0px;
                border-bottom-right-radius: 0px;
                padding-bottom: calc(${1.875 * BODY_FONT_SIZE}px + env(safe-area-inset-bottom, 0px));
                z-index: ${CONTENTS_TABLE_Z_INDEX};
            }

            ${theme.mediaQuery.extraSmall} {
                position: absolute;
                ${dockedBottomCss}
                left: 0;
                right: 0;
                width: 100%;
                pointer-events: auto;
                border-bottom-left-radius: 0px;
                border-bottom-right-radius: 0px;
                padding-bottom: calc(${1.875 * BODY_FONT_SIZE}px + env(safe-area-inset-bottom, 0px));
                z-index: ${CONTENTS_TABLE_Z_INDEX};
            }
        `;
    }}
`;

interface SlateContainerProps {
    canSubmit: boolean,
    readOnly: boolean,
    annotationIsEmpty: boolean,
    buttonCount: number,
    buttonLength: number,
    buttonMargin: number,
    buttonContainerPadding: number,
    dragContainerMargin: number,
    buttonContainerHeight: number,
    stationaryToolbarIsActive: boolean,
    isDragActive: boolean,
    isDragAccept: boolean,
    isDragReject: boolean,
    fontMultiplier: number,
    backgroundColor?: string,
    isCommentEditor: boolean,
}
const SlateContainer = styled.div<SlateContainerProps>`
    width: ${({
        isDragActive,
        stationaryToolbarIsActive,
        dragContainerMargin,
        buttonContainerPadding,
        buttonLength,
        buttonCount,
        canSubmit,
        buttonMargin,
        annotationIsEmpty,
    }) => {
        if (stationaryToolbarIsActive || !annotationIsEmpty) {
            return '100%';
        }

        if (isDragActive && !stationaryToolbarIsActive) {
            return `calc(100% - ${
                buttonLength * (canSubmit ? buttonCount : buttonCount - 1)
            }px - ${buttonMargin * (canSubmit ? buttonCount - 1 : buttonCount - 2)}px - ${dragContainerMargin}px - ${2 * buttonContainerPadding}px)`;
        }

        return `calc(100% - ${
            buttonLength * (canSubmit ? buttonCount : buttonCount - 1)
        }px - ${buttonContainerPadding * (canSubmit ? buttonCount - 1 : buttonCount - 2)}px)`;
    }};
    height: ${({ annotationIsEmpty, buttonContainerHeight }) => (annotationIsEmpty
        ? `${buttonContainerHeight}px`
        : 'auto'
    )};
    padding: ${`${0.3125 * BODY_FONT_SIZE}px ${0.625 * BODY_FONT_SIZE}px`};
    padding-left: ${({ readOnly }) => `${readOnly ? 0.625 * BODY_FONT_SIZE : 1.25 * BODY_FONT_SIZE}px`};
    padding-bottom: ${({ readOnly }) => `${readOnly ? 0 : 0.3125 * BODY_FONT_SIZE}px`};
    max-height: ${({ buttonContainerHeight, readOnly }) => (readOnly ? 'none' : `calc(100% - ${buttonContainerHeight}px)`)};
    font-size: ${({ fontMultiplier }) => `${fontMultiplier}em`};
    color: ${({ backgroundColor }) => (backgroundColor
        ? fontColorDiscriminator(backgroundColor)
        : 'inherit'
    )};
    overflow-y: ${({ readOnly }) => (readOnly ? 'visible' : 'scroll')};
    -webkit-overflow-scrolling: ${({ readOnly }) => (readOnly ? 'auto' : 'touch')}; /* smooth scrolling for iOS */
    touch-action: ${({ readOnly, isCommentEditor }) => {
        if (readOnly) return 'auto';
        if (isCommentEditor) return 'auto';
        return 'pan-y';
    }};
    background-color: ${({
        isDragAccept,
        isDragReject,
        backgroundColor,
        theme,
    }) => {
        if (isDragReject) {
            return setColorLightness(
                theme.color.error,
                95,
            );
        }

        if (isDragAccept) {
            return setColorLightness(
                theme.verascopeColor.blue200,
                95,
            );
        }

        if (backgroundColor) {
            return backgroundColor;
        }

        return theme.color.white;
    }};
    ${({ isDragActive, stationaryToolbarIsActive, dragContainerMargin }) => isDragActive
        && !stationaryToolbarIsActive && `
        margin-right: ${dragContainerMargin}px;
    `};
    border-radius: ${({ buttonContainerHeight }) => `${buttonContainerHeight / 2}px`};
    ${({ stationaryToolbarIsActive }) => stationaryToolbarIsActive && `
        border-bottom-left-radius: 0px;
        border-bottom-right-radius: 0px;
    `};
    transition: ${({ theme }) => `
        background-color 0.3s ${theme.motion.eagerEasing},
        margin-right 0.3s ${theme.motion.eagerEasing}
    `};

    &::-webkit-scrollbar {
        display: none;
    }
`;

interface ButtonContainerProps {
    height: number,
    padding: number,
    isEditing: boolean,
    readOnly: boolean,
    isCommentEditor: boolean,
    showAnnotationEditor: boolean,
}
const ButtonContainer = styled.div<ButtonContainerProps>`
    position: absolute;
    bottom: 0;
    right: 0;
    display: ${({ showAnnotationEditor }) => (showAnnotationEditor ? 'flex' : 'none')};
    justify-content: flex-end;
    align-items: flex-end;
    height: ${({ height }) => `${height}px`};
    border-radius: ${({ height }) => `${height / 2}px`};
    padding: ${({ padding }) => `${padding}px`};
    background: inherit;

    ${({ readOnly, isCommentEditor, theme }) => !readOnly && !isCommentEditor && `
        ${theme.mediaQuery.small} {
            right: ${1.25 * BODY_FONT_SIZE}px;
            padding-bottom: calc(${1.875 * BODY_FONT_SIZE}px + env(safe-area-inset-bottom, 0px));
        }

        ${theme.mediaQuery.extraSmall} {
            right: ${1.25 * BODY_FONT_SIZE}px;
            padding-bottom: calc(${1.875 * BODY_FONT_SIZE}px + env(safe-area-inset-bottom, 0px));
        }
    `}
`;

interface EditorButtonProps {
    isActive?: boolean,
    color?: string,
    length: number,
    padding: number,
    margin: number,
    customCursorActive: boolean,
}
const EditorButton = styled.div<EditorButtonProps>`
    position: relative;
    display: flex;
    align-items: center;
    justify-content: center;
    height: ${({ length }) => `${length}px`};
    width: ${({ length }) => `${length}px`};
    border-radius: ${({ length }) => `${length / 2}px`};
    background-color: ${({ isActive, color }) => (isActive && color
        ? setColorLightness(color, BUTTON_CONTAINER_LIGHTNESS_VALUE_HOVER)
        : 'transparent'
    )};
    padding: ${({ padding }) => `${padding}px`};
    margin-right: ${({ margin }) => `${margin}px`};
    transition: background-color 0.3s;
    cursor: ${({ customCursorActive }) => (customCursorActive ? 'none' : 'pointer')};

    &:last-child {
        margin-right: 0;
    }

    &:hover {
        background-color: ${({
        isActive,
        color,
        theme,
    }) => {
        if (isActive && color) {
            return setColorLightness(color, BUTTON_CONTAINER_LIGHTNESS_VALUE_HOVER);
        }

        if (isActive) {
            return theme.color.neutral400;
        }

        return theme.color.neutral300;
    }};
    }
`;

interface StationaryEditorContainerProps {
    isActive: boolean,
    stationaryEditorMinHeight: number
    transitionDuration: number,
    buttonLength: number,
    canSubmit: boolean,
    buttonContainerPadding: number,
    buttonCount: number,
    buttonMargin: number,
}
const StationaryEditorContainer = styled.div<StationaryEditorContainerProps>`
    position: relative;
    overflow: hidden;
    left: ${({ isActive }) => `${isActive
        ? 0
        : 1.875 * BODY_FONT_SIZE
    }px`};
    background-color: ${({ theme }) => theme.color.neutral100};
    flex-shrink: 0;
    width: ${({ isActive, buttonLength }) => (isActive
        ? '100%'
        : `calc(100% - ${buttonLength}px)`
    )};
    height: ${({ isActive }) => (isActive
        ? 'auto'
        : '0px'
    )};
    min-height: ${({ isActive, stationaryEditorMinHeight }) => `${isActive
        ? stationaryEditorMinHeight
        : 0
    }px`};
    padding-left: ${({ buttonMargin }) => `${buttonMargin / 2}px`};
    padding-bottom: ${({ isActive, buttonMargin }) => (isActive ? `${buttonMargin / 2}px` : '0px')};
    padding-right: ${({
        isActive,
        buttonLength,
        canSubmit,
        buttonContainerPadding,
        buttonCount,
        buttonMargin,
    }) => `${isActive
        ? `calc(${
            buttonLength * (canSubmit ? buttonCount : buttonCount - 1)
        }px + ${buttonMargin * (canSubmit ? buttonCount - 1 : buttonCount - 2)}px + ${
            2 * buttonContainerPadding
        }px)`
        : '0px'
    }`};
    border-bottom-left-radius: ${({ buttonLength }) => `${buttonLength}px`};
    border-bottom-right-radius: ${({ buttonLength }) => `${buttonLength}px`};
    background: inherit;
    transition: ${({ transitionDuration, theme }) => `
        min-height ${transitionDuration}ms ${theme.motion.delayEasing},
        width ${transitionDuration}ms ${theme.motion.delayEasing},
        left ${transitionDuration}ms ${theme.motion.delayEasing}
    `};

    ${({ theme }) => theme.mediaQuery.small} {
        padding-left: ${`${1.25 * BODY_FONT_SIZE}px`};
        padding-right: ${({
        isActive,
        buttonLength,
        canSubmit,
        buttonContainerPadding,
        buttonCount,
        buttonMargin,
    }) => `${isActive
        ? `calc(${
            buttonLength * (canSubmit ? buttonCount : buttonCount - 1)
        }px + ${buttonMargin * (canSubmit ? buttonCount - 1 : buttonCount - 2)}px + ${
            2 * buttonContainerPadding
        }px + ${1.25 * BODY_FONT_SIZE}px)`
        : `${1.25 * BODY_FONT_SIZE}px`
    }`};
    }

    ${({ theme }) => theme.mediaQuery.extraSmall} {
        padding-left: ${`${1.25 * BODY_FONT_SIZE}px`};
        padding-right: ${({
        isActive,
        buttonLength,
        canSubmit,
        buttonContainerPadding,
        buttonCount,
        buttonMargin,
    }) => `${isActive
        ? `calc(${
            buttonLength * (canSubmit ? buttonCount : buttonCount - 1)
        }px + ${buttonMargin * (canSubmit ? buttonCount - 1 : buttonCount - 2)}px + ${
            2 * buttonContainerPadding
        }px + ${1.25 * BODY_FONT_SIZE}px)`
        : `${1.25 * BODY_FONT_SIZE}px`
    }`};
    }
`;

interface EditorButtonIconProps {
    length: number,
}
const EditorButtonIcon = styled.div<EditorButtonIconProps>`
    width: ${({ length }) => `${length}px`};
    height: ${({ length }) => `${length}px`};

    & div {
        display: flex;
        align-items: center;
        justify-content: center;
        width: 100%;
        height: 100%;
    }

    & svg {
        width: 100%;
        height: 100%;
        fill: ${({
        color,
        theme,
    }) => (color || theme.color.neutral600)};
    }

    & path {
        transition: fill 0.3s;
    }
`;

interface SendButtonProps {
    stationaryToolbarIsActive: boolean,
    canSubmit: boolean,
    allowInteraction?: boolean,
    customCursorActive: boolean,
    length: number,
    isEditing: boolean | undefined,
    marginLeft?: boolean,
}
const SendButton = styled.div<SendButtonProps>`
    position: relative;
    display: flex;
    align-items: center;
    justify-content: center;
    height: ${({ length }) => `${length}px`};
    width: ${({ length }) => `${length}px`};
    border-radius: ${({ length }) => `${length / 2}px`};
    background-color: ${({
        stationaryToolbarIsActive,
        canSubmit,
        theme,
        color,
    }) => {
        if (canSubmit && color) {
            return color;
        }

        if (canSubmit) {
            return theme.verascopeColor.orange200;
        }

        if (stationaryToolbarIsActive) {
            return theme.color.neutral200;
        }

        return theme.color.neutral300;
    }};
    box-shadow: ${({ canSubmit, theme }) => (canSubmit
        ? theme.color.boxShadow300
        : 'none'
    )};
    margin-left: ${({ marginLeft }) => `${marginLeft ? 0.3125 * BODY_FONT_SIZE : 0}px`};
    padding: ${`${0.3125 * BODY_FONT_SIZE}px`};
    pointer-events: ${({ canSubmit, allowInteraction }) => ((canSubmit || allowInteraction)
        ? 'auto'
        : 'none'
    )};
    cursor: ${({ customCursorActive, canSubmit, allowInteraction }) => {
        if (customCursorActive) {
            return 'default';
        }

        if (canSubmit) {
            return 'pointer';
        }

        if (allowInteraction) {
            return 'not-allowed';
        }

        return 'default';
    }};
    transition: background-color 0.3s, box-shadow 0.3s;

    & div:has(> svg) {
        display: flex;
        align-items: center;
        justify-content: center;
        width: 100%;
        height: 100%;
    }

    & svg {
        fill: ${({ theme, canSubmit, stationaryToolbarIsActive }) => {
        if (canSubmit) {
            return theme.color.white;
        }

        if (stationaryToolbarIsActive) {
            return theme.color.neutral300;
        }

        return theme.color.neutral800;
    }};
        transform: ${({ isEditing }) => (isEditing ? 'none' : 'rotate(-90deg)')};
    }

    &:hover {
        box-shadow: ${({ canSubmit, theme }) => (canSubmit
        ? theme.color.boxShadow300
        : 'none'
    )};
    }
`;

interface EmojiSelectorContainerProps {
    height: number,
    width: number,
    isActive: boolean,
    stationaryToolbarHeight: number,
    stationaryEditorMinHeight: number,
}
const EmojiSelectorContainer = styled.div<EmojiSelectorContainerProps>`
    position: absolute;
    bottom: ${({ stationaryToolbarHeight, stationaryEditorMinHeight }) => `${stationaryToolbarHeight > stationaryEditorMinHeight
        ? stationaryToolbarHeight + 5
        : stationaryEditorMinHeight + 5
    }px`};
    right: 0px;
    display: flex;
    flex-direction: row;
    width: ${({ width }) => `${width}px`};
    max-width: ${`${15 * BODY_FONT_SIZE}px`};
    height: ${({ height }) => `${height}px`};
    border-radius: ${`${0.625 * BODY_FONT_SIZE}px`};
    box-shadow: ${({ theme }) => theme.color.boxShadow100};
    background-color: ${({ theme }) => theme.color.white};
    overflow: hidden;
    transition: ${({ theme }) => `
        bottom: 300ms ${theme.motion.eagerEasing}
    `};

    ${({ isActive }) => !isActive && `
        pointer-events: none;
    `};
`;

interface DropzoneCompactMessageContainerProps {
    stationaryToolbarIsActive: boolean,
    isDragActive: boolean,
    isDragReject: boolean,
}
const DropzoneCompactMessageContainer = styled.div<DropzoneCompactMessageContainerProps>`
    position: absolute;
    top: ${`${0.625 * BODY_FONT_SIZE}px`};
    left: ${({ stationaryToolbarIsActive }) => (stationaryToolbarIsActive
        ? 'auto'
        : `${0.625 * BODY_FONT_SIZE}px`
    )};
    right: ${({ stationaryToolbarIsActive }) => (stationaryToolbarIsActive
        ? `${0.625 * BODY_FONT_SIZE}px`
        : 'auto'
    )};
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: center;
    padding: ${`${0.3125 * BODY_FONT_SIZE}px ${0.9375 * BODY_FONT_SIZE}px`};
    padding-left: ${`${0.9375 * BODY_FONT_SIZE}px`};
    border-radius: ${`${1.25 * BODY_FONT_SIZE}px`};
    background-color: ${({
        theme,
        isDragReject,
    }) => {
        if (isDragReject) {
            return theme.color.error;
        }

        return theme.verascopeColor.blue200;
    }};
    pointer-events: ${({ isDragActive }) => (isDragActive
        ? 'auto'
        : 'none'
    )};
    box-shadow: ${({ theme }) => theme.color.boxShadow100};
`;

interface DropzoneCompactMessageIconProps {
    length: number,
}
export const DropzoneCompactMessageIcon = styled.div<DropzoneCompactMessageIconProps>`
    width: ${({ length }) => `${length}px`};
    height: ${({ length }) => `${length}px`};
    padding: ${`${0.15625 * BODY_FONT_SIZE}px`};
    margin-right: ${`${0.625 * BODY_FONT_SIZE}px`};

    & div {
        display: flex;
        align-items: center;
        justify-content: center;
        width: 100%;
        height: 100%;
    }

    & svg {
        width: 100%;
        height: 100%;
        fill: ${({ theme }) => theme.color.white};
    }
`;

export const DropzoneCompactMessageText = styled.div`
    font-family: ${FONT_TYPE.PLUS_JAKARTA_SANS};
    font-size: 1em;
    font-weight: 500;
    color: ${({ theme }) => theme.color.white};
`;

interface UploadProgressContainerProps {
    showEmojiSelector: boolean,
    emojiSelectorWidth: number,
    stationaryToolbarHeight: number,
    stationaryToolbarIsActive: boolean,
}
const UploadProgressContainer = styled.div<UploadProgressContainerProps>`
    position: absolute;
    top: ${({ stationaryToolbarIsActive }) => {
        if (!stationaryToolbarIsActive) {
            return `-${0.625 * BODY_FONT_SIZE}px`;
        }

        return 'auto';
    }};
    bottom: ${({ stationaryToolbarHeight, stationaryToolbarIsActive }) => {
        if (!stationaryToolbarIsActive) {
            return 'auto';
        }

        return `${stationaryToolbarHeight - 1.875 * BODY_FONT_SIZE}px`;
    }};
    left: ${({ stationaryToolbarIsActive }) => (stationaryToolbarIsActive
        ? 'auto'
        : `${0.9375 * BODY_FONT_SIZE}px`
    )};
    right: ${({ stationaryToolbarIsActive, showEmojiSelector, emojiSelectorWidth }) => {
        if (
            stationaryToolbarIsActive
            && showEmojiSelector
        ) {
            return `${emojiSelectorWidth + 1.25 * BODY_FONT_SIZE}px`;
        }

        if (stationaryToolbarIsActive) {
            return `${0.9375 * BODY_FONT_SIZE}px`;
        }

        return 'auto';
    }};
    transform: translateY(-100%);
    pointer-events: none;
    width: ${`${9.375 * BODY_FONT_SIZE}px`};
    transition: ${({ theme }) => `
        bottom: 300ms ${theme.motion.eagerEasing}
    `};
`;

interface CopyTextContainerProps {
    buttonLength: number,
    editorHeight: number,
    dockAboveEditor: boolean,
}
const CopyTextContainer = styled.div<CopyTextContainerProps>`
    position: absolute;
    bottom: ${({ buttonLength, editorHeight, dockAboveEditor }) => {
        if (dockAboveEditor) {
            return `calc(100% + ${0.625 * BODY_FONT_SIZE}px)`;
        }

        return `${(editorHeight - buttonLength) / 2}px`;
    }};
    right: ${({ buttonLength, dockAboveEditor }) => {
        if (dockAboveEditor) {
            return `${1.25 * BODY_FONT_SIZE}px`;
        }

        return `-${buttonLength + 0.3125 * BODY_FONT_SIZE}px`;
    }};
    width: ${({ buttonLength }) => `${buttonLength}px`};
    height: ${({ buttonLength }) => `${buttonLength}px`};
    border-radius: ${({ buttonLength }) => `${buttonLength / 2}px`};
    background: ${({ theme }) => theme.color.white};
    box-shadow: ${({ theme }) => theme.color.boxShadow300};
`;

const HiddenVoidContent = styled.span.attrs({
    style: HIDDEN_VOID_CONTENT_STYLE,
})``;

export default AnnotationEditor;
